/* SpawnAnything — voxel engine + game content:
   palette, voxel models, recipe (Bauplan) compiler, wand variants, weapons, unit types,
   meshers (culled faces, greedy LOD), comic outlines and army building. */
import * as THREE from 'three';
import { compileQuadruped, quadrupedModel } from './templates/quadruped.js';
import { fineBounds, fineFactor, headPrims, headRegion, meshWithFine, rasterize } from './heads.js';
import { faceModeOf } from './faceMode.js';

/* ---------------- palette ---------------- */
const T = -1, T2 = -2, TL = -3, P = -4;
const TEAMS = [
  { name: 'Blau', main: 0x3c78d8, dark: 0x2a55a0, light: 0x86b4ff, plasma: 0x6ff0ff, css: 'var(--blue-l)' },
  { name: 'Rot', main: 0xd8443c, dark: 0x9c2b26, light: 0xff9a8a, plasma: 0xff4f7a, css: 'var(--red-l)' },
];
const ROLE = { ROOT: 0, HEAD: 1, ARML: 2, ARMR: 3, LEGL: 4, LEGR: 5, TAIL: 6, JAW: 7, TURRET: 8, WINGL: 9, WINGR: 10, WAVE: 11 };
const STEEL = 0x9aa3ad, STEEL_D = 0x6f7882, STEEL_L = 0xc6cdd5, MAIL1 = 0x828a94, MAIL2 = 0x646b75, LEATH = 0x8a5a33, LEATH_D = 0x6b4426;
const BOOT = 0xcdb48a, BOOT_D = 0x9c8460, SLIT = 0x1c1f24, GOLD = 0xd9b23c, GOLD_D = 0xa8862a, BONE = 0xeadfc4;
const SKIN = 0xe3b48a, SKIN_D = 0xc4926a, CREST = 0xc8322b, CREST_D = 0x9a2420, DARK = 0x2b2e33, OLIVE = 0x5f6b3a, OLIVE_D = 0x4c5730, OLIVE_L = 0x75834a;

/* ---------------- voxel model ---------------- */
const KEY = (x, y, z) => (x + 512) | ((y + 512) << 10) | ((z + 512) << 20);
const DEC = k => [(k & 1023) - 512, ((k >> 10) & 1023) - 512, ((k >> 20) & 1023) - 512];
class VoxModel {
  constructor(vs) { this.vs = vs; this.map = new Map(); this.part = 0; this.parts = [{ role: ROLE.ROOT, pivot: [0, 0, 0], base: 0 }]; this.emit = 0; }
  addPart(role, py, pz, base = 0, px = 0) { this.parts.push({ role, pivot: [px, py, pz], base }); return this.parts.length - 1; }
  use(p) { this.part = p; return this; }
  glow(on) { this.emit = on ? 1 : 0; return this; }
  set(x, y, z, c) { this.map.set(KEY(x, y, z), { c, p: this.part, e: this.emit }); return this; }
  box(x0, y0, z0, x1, y1, z1, c) {
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) for (let z = z0; z <= z1; z++)
      this.set(x, y, z, typeof c === 'function' ? c(x, y, z) : c);
    return this;
  }
  sphere(cx, cy, cz, r, c, sy = 1) {
    const R = Math.ceil(r), RY = Math.ceil(r * sy);
    for (let x = -R; x <= R; x++) for (let y = -RY; y <= RY; y++) for (let z = -R; z <= R; z++) {
      if (x * x + (y / sy) * (y / sy) + z * z <= r * r + r * 0.6) this.set(cx + x, cy + y, cz + z, typeof c === 'function' ? c(cx + x, cy + y, cz + z) : c);
    }
    return this;
  }
}
const chk = (a, b) => (x, y, z) => ((x + y + z) & 1) ? a : b;
const hash3 = (x, y, z) => { let h = (x * 374761393 + y * 668265263 + z * 2147483647) | 0; h = (h ^ (h >>> 13)) * 1274126177; return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };

function humanoid(o) {
  const m = new VoxModel(o.vs || 0.09);
  const pHead = m.addPart(ROLE.HEAD, 14, 0);
  const pArmL = m.addPart(ROLE.ARML, 13.5, 0, o.armBase || 0);
  const pArmR = m.addPart(ROLE.ARMR, 13.5, 0, o.armBase || 0);
  const pLegL = m.addPart(ROLE.LEGL, 7, 0);
  const pLegR = m.addPart(ROLE.LEGR, 7, 0);
  m.use(pLegL).box(-3, 0, -2, -1, 6, 1, o.leg);
  m.use(pLegR).box(0, 0, -2, 2, 6, 1, o.leg);
  m.use(0).box(-3, 7, -2, 2, 13, 1, o.torso);
  m.use(pArmL).box(-5, 7, -1, -4, 13, 0, o.arm);
  m.use(pArmR).box(3, 7, -1, 4, 13, 0, o.arm);
  m.use(pHead).box(-3, 14, -3, 2, 19, 2, o.head);
  const p = { pHead, pArmL, pArmR, pLegL, pLegR };
  if (o.detail) o.detail(m, p);
  m.mountMelee = { part: pArmR, a: [3, 7, 1], f: [0, 0, 1], u: [0, 1, 0], s: [1, 0, 0] };
  m.mountRanged = { part: pArmR, a: [3, 6, -1], f: [0, -1, 0], u: [0, 0, 1], s: [1, 0, 0] };
  m.kind = 'humanoid'; m.atkStyle = 0; m.p = p;
  return m;
}

const MODELS = {
  ritter: () => humanoid({
    leg: (x, y, z) => y === 0 ? BOOT_D : y <= 3 ? BOOT : chk(MAIL1, MAIL2)(x, y, z),
    torso: chk(MAIL1, MAIL2),
    arm: (x, y, z) => y >= 12 ? STEEL : y <= 8 ? LEATH : chk(MAIL1, MAIL2)(x, y, z),
    head: STEEL,
    detail(m, p) {
      m.use(0).box(-1, 7, 1, 0, 13, 1, T).box(-2, 7, 1, -2, 12, 1, T2).box(1, 7, 1, 1, 12, 1, T2);
      m.box(-3, 9, -2, 2, 9, 1, LEATH).box(-1, 9, 1, 0, 9, 1, GOLD);
      m.use(p.pHead).box(-3, 14, -3, 2, 14, 2, MAIL2).box(-3, 16, 2, 2, 16, 2, SLIT).box(-1, 15, 2, 0, 17, 2, SLIT);
      m.box(-3, 19, -3, 2, 19, 2, STEEL_L).box(-2, 20, -2, 1, 20, 1, STEEL_L).box(-1, 21, -1, 0, 21, 0, STEEL_D);
      m.box(-1, 22, -2, 0, 23, 0, T).box(-1, 23, -4, 0, 24, -3, T).box(-1, 22, -5, 0, 22, -5, T2).box(-1, 24, -2, 0, 24, -1, TL);
      m.use(p.pArmL).box(-6, 12, -2, -4, 13, 1, STEEL);
      m.use(p.pArmR).box(3, 12, -2, 5, 13, 1, STEEL);
      m.use(p.pArmL).box(-7, 4, -3, -7, 11, 3, (x, y, z) => (y === 4 || y === 11 || z === -3 || z === 3) ? STEEL_D : T);
      m.box(-7, 6, 0, -7, 9, 0, BONE).box(-7, 7, -1, -7, 8, 1, BONE).set(-7, 7, 0, DARK);
    },
  }),
  legionaer: () => humanoid({
    leg: (x, y, z) => y <= 1 ? LEATH_D : (y === 3 || y === 5) && (x + z) % 2 === 0 ? LEATH : SKIN,
    torso: (x, y, z) => y <= 8 ? T : (y % 2 === 0 ? STEEL : STEEL_D),
    arm: (x, y, z) => y >= 12 ? STEEL : (y === 8 || y === 9) ? LEATH : SKIN,
    head: SKIN,
    detail(m, p) {
      m.use(0).box(-3, 7, -2, 2, 7, 1, (x) => (x & 1) ? LEATH : T2).box(-3, 9, -2, 2, 9, 1, LEATH).box(-1, 9, 1, 0, 9, 1, GOLD);
      m.use(p.pHead).set(-2, 16, 2, DARK).set(1, 16, 2, DARK).box(-1, 15, 2, 0, 15, 2, SKIN_D);
      m.box(-3, 18, -3, 2, 19, 2, STEEL).box(-3, 18, 2, 2, 18, 2, GOLD).box(-3, 14, -3, 2, 17, -3, STEEL_D);
      m.box(-3, 15, 1, -3, 17, 2, GOLD).box(2, 15, 1, 2, 17, 2, GOLD);
      m.box(-1, 20, -3, 0, 20, 2, GOLD_D).box(-1, 21, -4, 0, 23, 3, (x, y, z) => y === 23 || z === -4 || z === 3 ? CREST_D : CREST);
      m.use(p.pArmL).box(-6, 12, -2, -4, 13, 1, STEEL);
      m.use(p.pArmR).box(3, 12, -2, 5, 13, 1, STEEL);
      m.use(p.pArmL).box(-7, 3, -3, -7, 13, 3, (x, y, z) => (y === 3 || y === 13 || z === -3 || z === 3) ? GOLD_D : T);
      m.box(-8, 7, -1, -8, 9, 1, GOLD).box(-7, 4, 0, -7, 12, 0, (x, y) => y === 8 ? GOLD : T2);
    },
  }),
  soldat: () => humanoid({
    leg: (x, y, z) => y <= 1 ? DARK : OLIVE_D,
    torso: (x, y, z) => (y === 9 ? LEATH_D : OLIVE),
    arm: (x, y, z) => y <= 8 ? SKIN : OLIVE,
    head: SKIN,
    armBase: 0,
    detail(m, p) {
      m.use(0).box(-2, 10, 1, 1, 12, 1, OLIVE_L).box(-1, 11, 1, 0, 11, 1, T).box(-3, 8, -3, 2, 12, -3, LEATH_D).box(-2, 9, -4, 1, 11, -4, OLIVE_D);
      m.use(p.pHead).set(-2, 16, 2, DARK).set(1, 16, 2, DARK).box(-3, 18, -4, 2, 20, 3, (x, y, z) => y === 18 ? OLIVE_D : OLIVE_L).box(-1, 20, -1, 0, 20, 0, T);
      m.use(p.pArmL).box(-5, 12, -1, -5, 12, 0, T);
    },
  }),
  zombie: () => humanoid({
    leg: (x, y, z) => y === 0 ? DARK : (hash3(x, y, z) > 0.8 ? 0x7fa65a : 0x5b4636),
    torso: (x, y, z) => hash3(x, y, z) > 0.78 ? 0x7fa65a : (y === 9 ? T : 0x3d5a80),
    arm: (x, y, z) => y <= 10 ? 0x7fa65a : 0x3d5a80,
    head: (x, y, z) => hash3(x, y, z) > 0.85 ? 0x6b9150 : 0x7fa65a,
    armBase: -1.45,
    detail(m, p) {
      m.use(p.pHead).glow(1).set(-2, 16, 2, 0xff4a2a).set(1, 16, 2, 0xff4a2a).glow(0).box(-1, 15, 2, 0, 15, 2, 0x3a2a22).box(-3, 19, -3, -1, 19, 0, 0x3a3a2a).box(0, 19, 1, 2, 19, 2, 0x3a3a2a);
    },
  }),
  priester: () => humanoid({
    leg: (x, y, z) => y <= 1 ? 0x8a2a2a : 0xf3efe4,
    torso: (x, y, z) => 0xf3efe4,
    arm: (x, y, z) => y <= 7 ? SKIN : 0xf3efe4,
    head: SKIN,
    detail(m, p) {
      const W = 0xf3efe4, W2 = 0xe2dccb;
      m.use(0).box(-4, 2, -3, 3, 6, 2, (x, y, z) => (y === 2 ? GOLD : W2)).box(-1, 2, 2, 0, 13, 2, GOLD).box(-3, 10, -2, 2, 10, 1, T).box(-4, 12, -3, 3, 13, 2, T2);
      m.use(p.pHead).set(-2, 16, 2, DARK).set(1, 16, 2, DARK).box(-3, 14, -3, 2, 15, -3, 0xd9d9d9);
      m.box(-3, 20, -2, 2, 22, 1, W).box(-2, 23, -2, 1, 24, 1, W).box(-1, 25, -1, 0, 25, 0, W);
      m.box(-1, 20, 1, 0, 24, 1, GOLD).box(-3, 20, 1, 2, 20, 1, GOLD).box(-3, 20, -2, 2, 20, -2, GOLD);
    },
  }),
  huhn: () => {
    const m = new VoxModel(0.065);
    const WH = 0xf7f4ee, WH2 = 0xe4ded2, RD = 0xe0443a, OR = 0xf2a03a;
    const pHead = m.addPart(ROLE.HEAD, 8, 2);
    const pLegL = m.addPart(ROLE.LEGL, 3, 0.5), pLegR = m.addPart(ROLE.LEGR, 3, 0.5);
    const pWL = m.addPart(ROLE.WINGL, 6, 0, 0, -3), pWR = m.addPart(ROLE.WINGR, 6, 0, 0, 3);
    m.use(pLegL).box(-2, 0, 0, -2, 2, 0, OR).box(-2, 0, 1, -2, 0, 1, OR);
    m.use(pLegR).box(1, 0, 0, 1, 2, 0, OR).box(1, 0, 1, 1, 0, 1, OR);
    m.use(0).box(-3, 3, -3, 2, 7, 3, (x, y, z) => y === 3 ? WH2 : WH).box(-2, 6, -5, 1, 9, -4, WH).box(-1, 10, -5, 0, 10, -5, WH2);
    m.use(pHead).box(-2, 8, 1, 1, 11, 4, WH).box(-1, 12, 1, 0, 13, 3, RD).box(-1, 9, 5, 0, 10, 6, OR).box(-1, 7, 5, 0, 8, 5, RD).set(-2, 10, 3, 0x111111).set(1, 10, 3, 0x111111);
    m.use(pWL).box(-4, 4, -2, -4, 6, 2, T);
    m.use(pWR).box(3, 4, -2, 3, 6, 2, T);
    m.mountMelee = m.mountRanged = { part: pHead, a: [-1, 9, 7], f: [0, 0, 1], u: [0, 1, 0], s: [1, 0, 0] };
    m.kind = 'chicken'; m.atkStyle = 1;
    return m;
  },
  panzer: () => {
    const m = new VoxModel(0.11);
    const TR = 0x2b2d2a, WHL = 0x4a4d48;
    const pTur = m.addPart(ROLE.TURRET, 7, -1, 0, -0.5);
    m.use(0);
    m.box(-10, 0, -14, -7, 4, 13, (x, y, z) => (y === 4 ? 0x3a3c38 : TR));
    m.box(6, 0, -14, 9, 4, 13, (x, y, z) => (y === 4 ? 0x3a3c38 : TR));
    for (let z = -12; z <= 11; z += 5) { m.box(-10, 1, z, -10, 3, z + 2, WHL); m.box(9, 1, z, 9, 3, z + 2, WHL); m.set(-10, 2, z + 1, 0x6a6d68); m.set(9, 2, z + 1, 0x6a6d68); }
    m.box(-6, 2, -14, 5, 6, 13, (x, y, z) => (y === 6 && z % 6 === 0) ? OLIVE_D : OLIVE);
    m.box(-10, 5, -14, -7, 5, 13, OLIVE_D).box(6, 5, -14, 9, 5, 13, OLIVE_D);
    m.box(-6, 2, 14, 5, 4, 14, OLIVE_D).box(-5, 6, -14, 4, 6, -14, DARK);
    m.box(-8, 6, -12, -7, 7, -9, LEATH).box(6, 6, -12, 7, 7, -9, LEATH);
    m.use(pTur).box(-5, 7, -6, 4, 10, 4, (x, y, z) => (y === 10 && (x === -5 || x === 4)) ? OLIVE_D : OLIVE_L);
    m.box(-5, 9, -6, -5, 9, 4, T).box(4, 9, -6, 4, 9, 4, T).box(-3, 11, -4, -1, 11, -2, DARK).box(1, 11, -3, 2, 11, -2, OLIVE_D);
    m.box(3, 11, -5, 3, 16, -5, DARK);
    m.mountMelee = m.mountRanged = { part: pTur, a: [-1, 8, 5], f: [0, 0, 1], u: [0, 1, 0], s: [1, 0, 0] };
    m.kind = 'vehicle'; m.atkStyle = 2; m.turret = true;
    return m;
  },
  trex: () => {
    const m = new VoxModel(0.13);
    const G = 0x5e8c3a, G2 = 0x4a7230, BL = 0xa9c46a, TE = 0xf2efe6;
    const pHead = m.addPart(ROLE.HEAD, 26, 9), pJaw = m.addPart(ROLE.JAW, 24, 10);
    const pLegL = m.addPart(ROLE.LEGL, 14, -1), pLegR = m.addPart(ROLE.LEGR, 14, -1);
    const pTail = m.addPart(ROLE.TAIL, 17, -8, 0, -0.5);
    const pAL = m.addPart(ROLE.ARML, 18, 8), pAR = m.addPart(ROLE.ARMR, 18, 8);
    const skin = (x, y, z) => hash3(x, y, z) > 0.86 ? G2 : G;
    m.use(0).box(-6, 12, -8, 5, 22, 7, (x, y, z) => (y <= 14 && z >= -3) ? BL : skin(x, y, z));
    for (let z = -8; z <= 6; z += 2) m.box(-1, 23, z, 0, 24 + (z % 4 === 0 ? 1 : 0), z, T);
    m.box(-4, 20, 5, 3, 26, 10, skin);
    m.use(pLegL).box(-8, 6, -4, -5, 14, 2, skin).box(-7, 1, -2, -5, 6, 1, skin).box(-8, 0, -2, -4, 0, 4, G2);
    m.use(pLegR).box(4, 6, -4, 7, 14, 2, skin).box(4, 1, -2, 6, 6, 1, skin).box(3, 0, -2, 7, 0, 4, G2);
    m.use(pTail).box(-4, 14, -16, 3, 20, -9, skin).box(-3, 15, -24, 2, 18, -17, skin).box(-1, 16, -30, 0, 17, -25, G2);
    for (let z = -26; z <= -10; z += 3) m.box(-1, 19 + (z > -17 ? 2 : 0), z, 0, 19 + (z > -17 ? 2 : 0), z, T);
    m.use(pHead).box(-5, 25, 8, 4, 31, 20, skin).box(-5, 25, 12, -5, 25, 20, TE).box(4, 25, 12, 4, 25, 20, TE).box(-4, 25, 12, 3, 25, 20, 0x5a1a1a);
    m.glow(1).set(-5, 29, 16, 0xffd23c).set(4, 29, 16, 0xffd23c).glow(0).set(-2, 30, 20, DARK).set(1, 30, 20, DARK);
    m.use(pJaw).box(-4, 21, 9, 3, 24, 19, (x, y, z) => y === 24 && z >= 12 ? (x === -4 || x === 3 ? TE : 0x7a2222) : G2);
    m.use(pAL).box(-7, 16, 8, -6, 18, 10, skin);
    m.use(pAR).box(5, 16, 8, 6, 18, 10, skin);
    m.mountMelee = m.mountRanged = { part: pHead, a: [-1, 27, 21], f: [0, 0, 1], u: [0, 1, 0], s: [1, 0, 0] };
    m.kind = 'trex'; m.atkStyle = 1;
    return m;
  },
  mechhase: () => {
    const m = new VoxModel(0.085);
    const RD = 0xe0453c, RD2 = 0xf06a5e, RD3 = 0xb8342d, GR = 0x5a5a60, WH = 0xf3f1ee, PK = 0xf2a1b5;
    const pHead = m.addPart(ROLE.HEAD, 16, 0);
    const pFL = m.addPart(ROLE.LEGL, 9, 3), pFR = m.addPart(ROLE.LEGR, 9, 3);
    const pBL = m.addPart(ROLE.LEGR, 9, -3), pBR = m.addPart(ROLE.LEGL, 9, -3);
    const pGun = m.addPart(ROLE.ARMR, 10, 5);
    m.use(0).box(-6, 6, -5, 5, 15, 5, (x, y, z) => y === 15 ? RD2 : (y === 6 ? RD3 : (hash3(x, y, z) > 0.88 ? RD3 : RD)));
    m.glow(1).box(-4, 11, 5, -2, 12, 5, 0x9ff3ff).box(1, 11, 5, 3, 12, 5, 0x9ff3ff).glow(0);
    m.box(-6, 16, -6, 5, 25, -6, (x, y) => y === 25 ? RD2 : RD).box(-5, 17, -5, 4, 24, -5, DARK);
    m.box(-6, 9, -5, 5, 9, 5, T);
    m.use(pHead).box(-3, 16, -2, 2, 21, 3, WH).box(-3, 22, 0, -2, 28, 1, WH).box(1, 22, 0, 2, 28, 1, WH).box(-3, 23, 1, -2, 27, 1, PK).box(1, 23, 1, 2, 27, 1, PK);
    m.set(-2, 19, 3, DARK).set(1, 19, 3, DARK).set(-1, 18, 3, PK).set(0, 18, 3, PK).box(-3, 20, 3, -1, 20, 3, 0x2e9e4f);
    const leg = (p, sx, sz) => { m.use(p); m.box(sx < 0 ? -9 : 6, 7, sz < 0 ? -4 : 2, sx < 0 ? -7 : 8, 9, sz < 0 ? -2 : 4, RD); m.box(sx < 0 ? -10 : 8, 0, sz < 0 ? -4 : 3, sx < 0 ? -9 : 9, 6, sz < 0 ? -3 : 4, GR); m.box(sx < 0 ? -10 : 8, 0, sz < 0 ? -4 : 3, sx < 0 ? -9 : 9, 1, sz < 0 ? -3 : 4, DARK); };
    leg(pFL, -1, 1); leg(pFR, 1, 1); leg(pBL, -1, -1); leg(pBR, 1, -1);
    m.use(pGun).box(3, 9, 6, 4, 10, 9, GR).box(1, 7, 10, 6, 12, 16, (x, y, z) => (y === 12 && hash3(x, y, z) > 0.5) ? 0xdcd7cf : WH).box(2, 8, 16, 5, 11, 16, DARK);
    m.mountMelee = m.mountRanged = { part: pGun, a: [3, 9, 17], f: [0, 0, 1], u: [0, 1, 0], s: [1, 0, 0] };
    m.kind = 'mech'; m.atkStyle = 3;
    return m;
  },
};

/* ---------------- recipe units ("Bauplan"): primitives on a skeleton -> voxels, rig for free ---------------- */
const RECIPES = {
  ritter: { name: 'Ritter', template: 'humanoid', build: {},
    colors: { skin: '#e3b48a', torso: { color: '#828a94', checker: '#646b75' }, pelvis: '#6b4426', arms: { color: '#828a94', checker: '#646b75' }, hands: '#6f7882', legs: { color: '#646b75', checker: '#575d66' }, feet: '#9aa3ad' },
    face: { expression: 'entschlossen' }, hat: { type: 'helm_ritter', color: '#9aa3ad', color2: '#c6cdd5' },
    add: [
      // great helm from the headgear library (hair.js); crest: gold holder + big team plume
      { on: 'head', shape: 'cylinder', at: [0, 1.25, -0.1], size: [0.12, 0.16, 0.12], color: '#d9b23c' },
      { on: 'head', shape: 'ellipsoid', at: [0, 1.62, -0.4], size: [0.22, 0.55, 0.9], color: 'team' },
      // surcoat (team) front + back with gold cross, hanging front flap, gorget
      { on: 'torso', shape: 'box', at: [0, -0.1, 1.0], size: [0.66, 0.92, 0.07], color: 'team' },
      { on: 'torso', shape: 'box', at: [0, -0.1, -1.0], size: [0.66, 0.92, 0.07], color: 'team' },
      { on: 'torso', shape: 'box', at: [0, 0.1, 1.06], size: [0.12, 0.5, 0.05], color: '#d9b23c' },
      { on: 'torso', shape: 'box', at: [0, 0.3, 1.06], size: [0.46, 0.1, 0.05], color: '#d9b23c' },
      { on: 'pelvis', shape: 'box', at: [0, -1.6, 1.08], size: [0.55, 1.6, 0.07], color: 'team' },
      { on: 'torso', shape: 'box', at: [0, 0.95, 0], size: [0.72, 0.1, 0.92], color: '#c6cdd5' },
      // sword belt, buckle, scabbard
      { on: 'torso', shape: 'box', at: [0, -0.78, 0], size: [1.06, 0.1, 1.06], color: '#6b4426' },
      { on: 'torso', shape: 'box', at: [0, -0.78, 1.1], size: [0.15, 0.12, 0.05], color: '#d9b23c' },
      { on: 'torso', shape: 'box', at: [-0.95, -1.5, 0.3], size: [0.1, 0.75, 0.2], rot: [-15, 0, 0], color: '#3a2a22' },
      // pauldrons (two lames), vambraces, gauntlet cuffs
      { on: 'arms', shape: 'box', at: [0.2, 0.86, 0], size: [1.5, 0.2, 1.45], color: '#9aa3ad' },
      { on: 'arms', shape: 'box', at: [0.25, 0.62, 0], size: [1.42, 0.12, 1.38], color: '#c6cdd5' },
      { on: 'arms', shape: 'box', at: [0, -0.4, 0], size: [1.14, 0.28, 1.14], color: '#9aa3ad' },
      { on: 'arms', shape: 'box', at: [0, -0.68, 0], size: [1.22, 0.06, 1.22], color: '#c6cdd5' },
      // knee cops, greaves, pointed sabatons
      { on: 'legs', shape: 'box', at: [0, 0.02, 1.02], size: [0.72, 0.15, 0.18], color: '#c6cdd5' },
      { on: 'legs', shape: 'box', at: [0, -0.48, 0], size: [1.1, 0.34, 1.1], color: '#9aa3ad' },
      { on: 'legs', shape: 'box', at: [0, -0.92, 1.58], size: [0.55, 0.08, 0.2], color: '#c6cdd5' },
      // heater shield on the left arm: steel rim, team face, gold cross, pointed bottom
      { on: 'armL', shape: 'box', at: [-1.72, -0.05, 0.2], size: [0.2, 0.78, 2.2], color: '#6f7882' },
      { on: 'armL', shape: 'cone', at: [-1.72, -1.05, 0.2], size: [0.2, 0.32, 2.2], rot: [180, 0, 0], color: '#6f7882' },
      { on: 'armL', shape: 'box', at: [-1.9, -0.05, 0.2], size: [0.06, 0.7, 1.95], color: 'team' },
      { on: 'armL', shape: 'box', at: [-1.97, -0.1, 0.2], size: [0.05, 0.55, 0.3], color: '#d9b23c' },
      { on: 'armL', shape: 'box', at: [-1.97, 0.15, 0.2], size: [0.05, 0.12, 1.3], color: '#d9b23c' },
    ] },
  legionaer: { name: 'Legionär', template: 'humanoid', build: {},
    colors: { skin: '#e3b48a', torso: { color: '#9aa3ad', stripes: '#6f7882' }, pelvis: 'team', arms: '#e3b48a', hands: '#e3b48a', legs: '#e3b48a', feet: '#6b4426' },
    face: { expression: 'entschlossen', eyeColor: '#5a3a1a', brows: 'dick', nose: 'haken', beard: 'stoppeln', beardColor: '#3a2416' }, hair: { style: 'kurz', color: '#3a2416' }, hat: { type: 'helm_roemisch', color: '#9aa3ad', color2: '#c8322b' },
    add: [
      // red tunic skirt (team) + leather pteruges apron
      { on: 'pelvis', shape: 'box', at: [0, -1.4, 0], size: [1.12, 1.6, 1.12], color: 'team' },
      { on: 'pelvis', shape: 'box', at: [0, -1.7, 1.1], size: [0.55, 1.4, 0.06], color: '#6b4426', stripes: { color: '#d9b23c', axis: 'x', period: 0.066 } },
      // lorica: shoulder plates + rim, brass hinges, neck scarf, belt with studs
      { on: 'arms', shape: 'box', at: [0.15, 0.8, 0], size: [1.42, 0.3, 1.42], color: '#9aa3ad' },
      { on: 'arms', shape: 'box', at: [0.2, 0.55, 0], size: [1.4, 0.08, 1.38], color: '#6f7882' },
      { on: 'torso', shape: 'box', at: [0.35, 0.4, 1.04], size: [0.12, 0.12, 0.05], mirror: true, color: '#d9b23c' },
      { on: 'torso', shape: 'box', at: [0, 0.4, 1.04], size: [0.06, 0.45, 0.05], color: '#d9b23c' },
      { on: 'torso', shape: 'box', at: [0, 0.92, 0.3], size: [0.62, 0.12, 0.85], color: '#f2ecd8' },
      { on: 'torso', shape: 'box', at: [0, -0.84, 0], size: [1.06, 0.1, 1.06], color: '#6b4426' },
      { on: 'torso', shape: 'box', at: [0, -0.84, 1.08], size: [0.62, 0.08, 0.05], color: '#d9b23c', stripes: { color: '#6b4426', axis: 'x', period: 0.066 } },
      // leather bracers, sandal straps
      { on: 'arms', shape: 'box', at: [0, -0.55, 0], size: [1.1, 0.14, 1.1], color: '#6b4426' },
      { on: 'legs', shape: 'box', at: [0, -0.66, 0], size: [1.04, 0.2, 1.04], color: '#e3b48a', stripes: { color: '#6b4426', axis: 'y', period: 0.066 } },
      // scutum on the left arm: gold rim, team face, steel boss, gold wings
      { on: 'armL', shape: 'box', at: [-1.72, -0.2, 0.2], size: [0.22, 1.36, 2.5], color: '#d9b23c' },
      { on: 'armL', shape: 'box', at: [-1.85, -0.2, 0.2], size: [0.12, 1.26, 2.36], color: 'team' },
      { on: 'armL', shape: 'cylinder', axis: 'x', at: [-2.0, -0.2, 0.2], size: [0.12, 0.28, 0.65], color: '#c6cdd5' },
      { on: 'armL', shape: 'box', at: [-1.98, 0.55, 0.2], size: [0.05, 0.06, 1.7], color: '#d9b23c' },
      { on: 'armL', shape: 'box', at: [-1.98, -0.95, 0.2], size: [0.05, 0.06, 1.7], color: '#d9b23c' },
    ] },
  soldat: { name: 'Soldat', template: 'humanoid', build: {},
    colors: { skin: '#e3b48a', torso: '#5f6b3a', pelvis: '#4c5730', arms: '#5f6b3a', hands: '#e3b48a', legs: '#4c5730', feet: '#2b2e33' },
    face: { expression: 'entschlossen', eyeColor: '#3a5a8a', beard: 'stoppeln', beardColor: '#6a5040' }, hair: { style: 'kurz', color: '#5a3a22' }, hat: { type: 'helm_stahl', color: '#4f5d32', color2: '#3e4a26' },
    add: [
      // uniform: collar, placket buttons, chest pockets with flaps, dog tags
      { on: 'torso', shape: 'box', at: [0.3, 0.88, 0.85], size: [0.32, 0.12, 0.2], mirror: true, rot: [0, 0, -12], color: '#75834a' },
      { on: 'torso', shape: 'box', at: [0, 0.1, 1.03], size: [0.05, 0.75, 0.05], color: '#3e4a26', stripes: { color: '#2b2e33', axis: 'y', period: 0.066 } },
      { on: 'torso', shape: 'box', at: [0.45, 0.25, 1.03], size: [0.28, 0.25, 0.06], mirror: true, color: '#75834a' },
      { on: 'torso', shape: 'box', at: [0.45, 0.45, 1.07], size: [0.3, 0.08, 0.05], mirror: true, color: '#4c5730' },
      { on: 'torso', shape: 'box', at: [-0.15, 0.62, 1.04], size: [0.06, 0.1, 0.04], color: '#c6cdd5' },
      // webbing belt, buckle, ammo pouches, canteen, backpack with bedroll
      { on: 'torso', shape: 'box', at: [0, -0.8, 0], size: [1.07, 0.12, 1.07], color: '#8a7a54' },
      { on: 'torso', shape: 'box', at: [0, -0.8, 1.1], size: [0.14, 0.12, 0.05], color: '#9aa3ad' },
      { on: 'torso', shape: 'box', at: [0.6, -0.72, 1.12], size: [0.22, 0.22, 0.14], mirror: true, color: '#7a6a44' },
      { on: 'torso', shape: 'cylinder', at: [1.02, -0.85, 0], size: [0.14, 0.24, 0.5], color: '#3e4a26' },
      { on: 'torso', shape: 'box', at: [0, 0.05, -1.3], size: [0.8, 0.72, 0.32], color: '#5a4a32' },
      { on: 'torso', shape: 'cylinder', axis: 'x', at: [0, 0.88, -1.3], size: [0.85, 0.18, 0.28], color: '#3e4a26' },
      { on: 'torso', shape: 'box', at: [0.35, 0.3, -1.05], size: [0.08, 0.9, 0.06], mirror: true, color: '#8a7a54' },
      // sleeves: team armband, rolled cuffs
      { on: 'armL', shape: 'box', at: [0, 0.38, 0], size: [1.1, 0.12, 1.1], color: 'team' },
      { on: 'arms', shape: 'box', at: [0, -0.52, 0], size: [1.1, 0.1, 1.1], color: '#75834a' },
      // trousers bloused into boots, knee patches, boot laces
      { on: 'legs', shape: 'box', at: [0, -0.58, 0], size: [1.08, 0.3, 1.08], color: '#2b2e33' },
      { on: 'legs', shape: 'box', at: [0, -0.28, 0], size: [1.14, 0.08, 1.14], color: '#4c5730' },
      { on: 'legs', shape: 'box', at: [0, 0.05, 1.03], size: [0.6, 0.15, 0.06], color: '#5f6b3a' },
      { on: 'legs', shape: 'box', at: [0, -0.6, 1.1], size: [0.25, 0.25, 0.04], color: '#2b2e33', stripes: { color: '#8a7a54', axis: 'y', period: 0.035 } },
    ] },
  zombie: { name: 'Zombie', template: 'humanoid', build: { armForward: true },
    colors: { skin: '#7fa65a', torso: '#3d5a80', pelvis: '#5b4636', arms: '#3d5a80', hands: '#7fa65a', legs: '#5b4636', feet: '#2b2e33' },
    face: { type: 'monster', expression: 'irre', eyes: 'gluehend', eyeColor: '#ff4a2a', brows: 'keine', nose: 'stups', mouth: 'schreien', lipColor: '#4a6a3a', marks: ['naht', 'dreck'], ears: 'klein' },
    add: [
      // face from the painter (faces.js); messy hair tufts and exposed brain patch
      { on: 'head', shape: 'box', at: [-0.4, 1.0, -0.2], size: [0.55, 0.14, 0.75], color: '#3a3a2a' },
      { on: 'head', shape: 'box', at: [0.55, 1.02, -0.55], size: [0.4, 0.12, 0.4], color: '#3a3a2a' },
      { on: 'head', shape: 'ellipsoid', at: [0.4, 1.0, 0.3], size: [0.38, 0.14, 0.35], color: '#e88aa0' },
      { on: 'head', shape: 'box', at: [-0.2, 0.2, -1.04], size: [0.7, 0.75, 0.08], color: '#3a3a2a' },
      // torn shirt: holes with skin, ragged hem, ripped sleeve, loose tie (team)
      { on: 'torso', shape: 'box', at: [0.45, -0.3, 1.0], size: [0.25, 0.25, 0.06], color: '#7fa65a' },
      { on: 'torso', shape: 'box', at: [-0.5, 0.35, 1.0], size: [0.2, 0.2, 0.06], color: '#7fa65a' },
      { on: 'torso', shape: 'box', at: [-0.42, 0.35, 1.04], size: [0.06, 0.12, 0.04], color: '#eadfc4' },
      { on: 'torso', shape: 'box', at: [0.3, 0.2, -1.0], size: [0.3, 0.35, 0.06], color: '#7fa65a' },
      { on: 'torso', shape: 'box', at: [0, -0.92, 0], size: [1.07, 0.1, 1.07], color: '#3d5a80', stripes: { color: '#7fa65a', axis: 'x', period: 0.066 } },
      { on: 'torso', shape: 'box', at: [0, 0.88, 0.6], size: [0.42, 0.1, 0.5], color: '#e8e2d2' },
      { on: 'torso', shape: 'box', at: [0.12, 0.2, 1.07], size: [0.13, 0.6, 0.05], rot: [0, 0, -12], color: 'team' },
      { on: 'torso', shape: 'box', at: [0, -0.72, 0], size: [1.06, 0.08, 1.06], color: '#3a2a22' },
      { on: 'armR', shape: 'box', at: [0, -0.35, 0], size: [1.08, 0.35, 1.08], color: '#7fa65a', checker: '#6b9150' },
      { on: 'armL', shape: 'box', at: [0, -0.62, 0], size: [1.12, 0.1, 1.12], color: '#2f4766' },
      { on: 'armR', shape: 'box', at: [0, -0.2, 1.0], size: [0.3, 0.1, 0.08], color: '#e8e2c8' },
      // torn trousers, bare left foot, patch, exposed shin bone
      { on: 'legs', shape: 'box', at: [0, -0.48, 0], size: [1.07, 0.08, 1.07], color: '#5b4636', stripes: { color: '#7fa65a', axis: 'x', period: 0.066 } },
      { on: 'legR', shape: 'box', at: [0, -0.68, 0], size: [1.04, 0.2, 1.04], color: '#7fa65a' },
      { on: 'legL', shape: 'box', at: [0, -0.15, 1.02], size: [0.5, 0.2, 0.06], color: '#7a6048' },
      { on: 'legR', shape: 'box', at: [0, -0.65, 1.02], size: [0.25, 0.25, 0.05], color: '#eadfc4' },
      { on: 'legR', shape: 'box', at: [0, -0.88, 0.28], size: [1.08, 0.14, 1.32], color: '#7fa65a' },
    ] },
  priester: { name: 'Hohepriester', template: 'humanoid', build: {},
    colors: { skin: '#e3b48a', torso: '#f3efe4', pelvis: '#f3efe4', arms: '#f3efe4', hands: '#e3b48a', legs: '#f3efe4', feet: '#8a2a2a' },
    face: { expression: 'froh', eyes: 'muede', eyeColor: '#2a4fb0', brows: 'buschig', browColor: '#f4f4f2', nose: 'knubbel', beard: 'langer_bart', beardColor: '#eeeeee', age: 'alt', cheeks: 'rouge' }, hair: { style: 'haarkranz', color: '#e8e8e8' }, hat: { type: 'mitra', color: '#f6f3ea', color2: '#d9b23c' },
    add: [
      // long robe with gold hem and front band, rope belt, team stole with crosses
      { on: 'pelvis', shape: 'box', at: [0, -3.9, 0], size: [1.25, 4.9, 1.25], color: '#e2dccb' },
      { on: 'torso', shape: 'box', at: [0, -3.57, 0], size: [1.24, 0.1, 1.24], color: '#d9b23c' },
      { on: 'pelvis', shape: 'box', at: [0, -3.9, 1.27], size: [0.22, 4.9, 0.06], color: '#d9b23c' },
      { on: 'torso', shape: 'box', at: [0, -0.62, 0], size: [1.06, 0.1, 1.06], color: '#d9b23c' },
      { on: 'torso', shape: 'box', at: [0.25, -1.4, 1.1], size: [0.06, 0.7, 0.06], color: '#d9b23c' },
      { on: 'torso', shape: 'box', at: [0.4, -0.1, 1.04], size: [0.2, 1.05, 0.06], mirror: true, color: 'team' },
      { on: 'pelvis', shape: 'box', at: [0.4, -2.4, 1.31], size: [0.2, 2.6, 0.06], mirror: true, color: 'team' },
      { on: 'pelvis', shape: 'box', at: [0.4, -4.4, 1.36], size: [0.06, 0.6, 0.04], mirror: true, color: '#d9b23c' },
      { on: 'pelvis', shape: 'box', at: [0.4, -4.3, 1.36], size: [0.18, 0.15, 0.04], mirror: true, color: '#d9b23c' },
      { on: 'torso', shape: 'box', at: [0, 0.82, 0], size: [1.14, 0.25, 1.14], color: '#d9b23c', stripes: { color: '#f3efe4', axis: 'y', period: 0.035 } },
      // wide sleeves with gold cuffs
      { on: 'arms', shape: 'box', at: [0, -0.55, 0], size: [1.35, 0.32, 1.35], color: '#f3efe4' },
      { on: 'arms', shape: 'box', at: [0, -0.78, 0], size: [1.4, 0.08, 1.4], color: '#d9b23c' },
      { on: 'head', shape: 'box', at: [0.35, 0.0, -1.1], size: [0.18, 0.75, 0.06], mirror: true, color: '#d9b23c' },
      // crosier on the left arm: gold staff with crook
      { on: 'armL', shape: 'cylinder', at: [0, 0.4, 1.3], size: [0.16, 3.2, 0.16], color: '#d9b23c' },
      { on: 'armL', shape: 'cylinder', axis: 'z', at: [0, 3.75, 1.9], size: [0.16, 0.16, 0.7], color: '#d9b23c' },
      { on: 'armL', shape: 'cylinder', at: [0, 3.35, 2.5], size: [0.16, 0.45, 0.16], color: '#d9b23c' },
    ] },
  trump: { name: 'Donald Trump', template: 'humanoid', build: { width: 1.2 },
    colors: { skin: '#f0a066', torso: '#1f2a44', pelvis: '#1f2a44', arms: '#1f2a44', hands: '#f0b07a', legs: '#1f2a44', feet: '#151515' },
    face: { expression: 'arrogant', eyes: 'schmal', eyeColor: '#4a6a9a', brows: 'normal', browColor: '#e0b860', nose: 'breit', mouth: 'schmollmund', lipColor: '#c4604a', cheeks: 'pausbacken', marks: ['lidschatten'], color: '#f6d6b4' }, hair: { style: 'tolle', color: '#f2d27a' },
    add: [
      // white shirt, wide lapels, collar points, flag pin, buttons, pocket square
      { on: 'torso', shape: 'box', at: [0, 0.5, 1.0], size: [0.4, 0.5, 0.08], color: '#f4f4f2' },
      { on: 'torso', shape: 'box', at: [0.32, 0.45, 1.04], size: [0.2, 0.58, 0.06], mirror: true, rot: [0, 0, -18], color: '#2a3656' },
      { on: 'torso', shape: 'box', at: [0.18, 0.9, 1.05], size: [0.16, 0.1, 0.06], mirror: true, rot: [0, 0, 25], color: '#f4f4f2' },
      { on: 'torso', shape: 'box', at: [-0.55, 0.45, 1.1], size: [0.08, 0.06, 0.04], color: '#c8102e', stripes: { color: '#f4f4f2', axis: 'y', period: 0.035 } },
      { on: 'torso', shape: 'box', at: [0.55, 0.3, 1.08], size: [0.13, 0.08, 0.05], color: '#f4f4f2' },
      { on: 'torso', shape: 'box', at: [0.22, -0.55, 1.04], size: [0.06, 0.06, 0.05], color: '#0e1424' },
      { on: 'torso', shape: 'box', at: [0.65, -0.75, 1.03], size: [0.25, 0.04, 0.05], mirror: true, color: '#141c30' },
      // signature: extra-long red tie with knot, hanging past the belt
      { on: 'torso', shape: 'box', at: [0, 0.82, 1.08], size: [0.16, 0.1, 0.07], color: '#a50d25' },
      { on: 'torso', shape: 'box', at: [0, -0.15, 1.08], size: [0.15, 0.9, 0.07], color: '#c8102e' },
      { on: 'pelvis', shape: 'box', at: [0, -1.4, 1.12], size: [0.16, 1.6, 0.07], color: '#c8102e' },
      // oversized suit: boxy jacket hem, shoulder pads, white cuffs, trouser crease, shiny shoes
      { on: 'pelvis', shape: 'box', at: [0, -1.2, 0], size: [1.08, 1.2, 1.08], color: '#1f2a44' },
      { on: 'arms', shape: 'box', at: [0.1, 0.85, 0], size: [1.25, 0.22, 1.2], color: '#24304e' },
      { on: 'arms', shape: 'box', at: [0, -0.7, 0], size: [1.08, 0.08, 1.08], color: '#f4f4f2' },
      { on: 'legs', shape: 'box', at: [0, -0.2, 1.02], size: [0.06, 0.65, 0.05], color: '#2a3656' },
      { on: 'legs', shape: 'box', at: [0, -0.84, 1.45], size: [0.6, 0.05, 0.18], color: '#4a4a4a' },
    ] },
  megamind: { name: 'Megamind', template: 'humanoid', build: { width: 0.9, headScale: 1.05 },
    colors: { skin: '#4a7fd6', torso: '#16161b', pelvis: '#16161b', arms: '#16161b', hands: '#b8bec8', legs: '#16161b', feet: '#26262c' },
    face: { expression: 'arrogant', eyes: 'gross', eyeColor: '#3fae5a', brows: 'duenn', browColor: '#1f3a80', nose: 'klein', mouth: 'schief', lipColor: '#2a4a90', beard: 'kinnbart', beardColor: '#0d0d0d', ears: 'klein' },
    add: [
      // signature: gigantic bulging blue cranium with brow ridge and veins
      { on: 'head', shape: 'ellipsoid', at: [0, 0.75, -0.25], size: [1.55, 1.45, 1.6], color: '#4a7fd6' },
      { on: 'head', shape: 'ellipsoid', at: [0, 1.4, 0.2], size: [0.9, 0.5, 0.9], color: '#5a8fe6' },
      { on: 'head', shape: 'box', at: [-0.7, 1.2, 0.9], size: [0.05, 0.35, 0.05], rot: [0, 0, 25], color: '#3a6ac0' },
      // signature: tall silver spiked collar and dark cape
      { on: 'torso', shape: 'box', at: [0, 1.3, -0.75], size: [1.3, 0.85, 0.12], color: '#b8bec8' },
      { on: 'torso', shape: 'box', at: [1.05, 1.75, -0.4], size: [0.42, 1.05, 0.12], mirror: true, rot: [0, 25, -28], color: '#b8bec8' },
      { on: 'torso', shape: 'cone', at: [1.45, 2.7, -0.3], size: [0.14, 0.35, 0.14], mirror: true, rot: [0, 0, -28], color: '#d0d6de' },
      { on: 'torso', shape: 'box', at: [0, -0.35, -1.15], size: [1.25, 1.35, 0.07], color: '#3b1f66' },
      { on: 'pelvis', shape: 'box', at: [0, -3.4, -1.45], size: [1.4, 3.4, 0.08], color: '#3b1f66' },
      { on: 'pelvis', shape: 'box', at: [0, -3.4, -1.38], size: [1.3, 3.3, 0.06], color: '#16161b' },
      // suit: glowing chest emblem, piping, belt (team), silver gauntlets and shoulder spikes, boot tops
      { on: 'torso', shape: 'box', at: [0, 0.2, 1.03], size: [0.45, 0.3, 0.05], color: '#2a2a3a' },
      { on: 'torso', shape: 'box', at: [0, 0.2, 1.07], size: [0.3, 0.08, 0.04], color: '#3a8cff', glow: true },
      { on: 'torso', shape: 'box', at: [0, 0.2, 1.07], size: [0.08, 0.2, 0.04], color: '#3a8cff', glow: true },
      { on: 'torso', shape: 'box', at: [0.75, -0.1, 1.04], size: [0.04, 0.8, 0.04], mirror: true, color: '#4a4f66' },
      { on: 'torso', shape: 'box', at: [0, -0.62, 0], size: [1.06, 0.12, 1.06], color: 'team' },
      { on: 'torso', shape: 'box', at: [0, -0.62, 1.1], size: [0.18, 0.14, 0.05], color: '#b8bec8' },
      { on: 'arms', shape: 'cone', at: [0.3, 1.25, 0], size: [0.9, 0.55, 0.9], color: '#b8bec8' },
      { on: 'arms', shape: 'box', at: [0, -0.55, 0], size: [1.2, 0.3, 1.2], color: '#b8bec8' },
      { on: 'arms', shape: 'box', at: [0, -0.35, 0], size: [1.3, 0.06, 1.3], color: '#8a909a' },
      { on: 'legs', shape: 'box', at: [0, -0.55, 0], size: [1.12, 0.35, 1.12], color: '#26262c' },
      { on: 'legs', shape: 'box', at: [0, -0.2, 0], size: [1.16, 0.06, 1.16], color: '#b8bec8' },
    ] },
  optimus: { name: 'Optimus Prime', template: 'humanoid', build: { width: 1.3 },
    colors: { skin: '#c6cdd5', torso: '#c8102e', pelvis: '#9aa3ad', arms: '#c8102e', hands: '#3a6ac0', legs: '#1f4fb5', feet: '#9aa3ad' },
    face: { type: 'roboter', expression: 'entschlossen', eyes: 'led', eyeColor: '#6fd4ff', mouth: 'maske', color: '#c6cdd5' },
    add: [
      // head: blue helmet around the silver face (robot face from faces.js: glowing eyes, vented faceplate), crest, antennas, ear horns
      { on: 'head', shape: 'box', at: [0, 0.62, -0.1], size: [1.14, 0.45, 1.04], color: '#1f4fb5' },
      { on: 'head', shape: 'box', at: [0, 0.1, -0.6], size: [1.14, 0.95, 0.5], color: '#1f4fb5' },
      { on: 'head', shape: 'box', at: [1.0, -0.05, 0.25], size: [0.16, 0.9, 0.78], mirror: true, color: '#1f4fb5' },
      { on: 'head', shape: 'box', at: [0, 1.0, 0.2], size: [0.18, 0.25, 0.9], color: '#1f4fb5' },
      { on: 'head', shape: 'box', at: [0, 0.75, 1.0], size: [0.3, 0.22, 0.08], color: '#c6cdd5' },
      { on: 'head', shape: 'box', at: [1.15, 0.7, 0], size: [0.12, 0.8, 0.14], mirror: true, color: '#1f4fb5' },
      { on: 'head', shape: 'box', at: [1.15, 1.55, 0], size: [0.06, 0.12, 0.06], mirror: true, color: '#c6cdd5' },
      { on: 'head', shape: 'box', at: [1.2, 0.1, 0.1], size: [0.1, 0.25, 0.3], mirror: true, color: '#c6cdd5' },
      // cab chest: two windows with frames, chrome grille, headlights, autobot badge
      { on: 'torso', shape: 'box', at: [0.47, 0.42, 1.0], size: [0.44, 0.42, 0.08], mirror: true, color: '#9aa3ad' },
      { on: 'torso', shape: 'box', at: [0.47, 0.44, 1.05], size: [0.36, 0.34, 0.05], mirror: true, color: '#6ec6ff' },
      { on: 'torso', shape: 'box', at: [0.4, 0.55, 1.09], size: [0.14, 0.1, 0.03], mirror: true, color: '#c8f0ff' },
      { on: 'torso', shape: 'box', at: [0, -0.38, 1.04], size: [0.55, 0.32, 0.06], color: '#c6cdd5', stripes: { color: '#5a616b', axis: 'x', period: 0.035 } },
      { on: 'torso', shape: 'box', at: [0.75, -0.38, 1.05], size: [0.15, 0.12, 0.05], mirror: true, color: '#fff6c8', glow: true },
      { on: 'torso', shape: 'box', at: [0, 0.08, 1.06], size: [0.12, 0.1, 0.04], color: '#c8102e' },
      { on: 'torso', shape: 'box', at: [0, -0.88, 0], size: [1.05, 0.12, 1.05], color: 'team' },
      // smokestacks behind the shoulders, red shoulder blocks
      { on: 'arms', shape: 'cylinder', at: [-0.2, 1.55, -0.7], size: [0.3, 0.75, 0.3], color: '#c6cdd5' },
      { on: 'arms', shape: 'cylinder', at: [-0.2, 2.25, -0.7], size: [0.34, 0.06, 0.34], color: '#6f7882' },
      { on: 'arms', shape: 'box', at: [0.15, 0.72, 0], size: [1.35, 0.3, 1.25], color: '#c8102e' },
      { on: 'arms', shape: 'box', at: [0.15, 0.4, 0], size: [1.3, 0.06, 1.2], color: '#9aa3ad' },
      { on: 'arms', shape: 'box', at: [0, -0.4, 0], size: [1.15, 0.3, 1.15], color: '#1f4fb5' },
      { on: 'arms', shape: 'box', at: [1.1, -0.4, 0], size: [0.06, 0.18, 0.5], color: '#c6cdd5' },
      // legs: silver hips, knee plates, wheels, blue shins with vents, feet
      { on: 'pelvis', shape: 'box', at: [0, 0, 1.0], size: [0.6, 0.8, 0.08], color: '#c6cdd5' },
      { on: 'legs', shape: 'box', at: [0, 0.15, 1.02], size: [0.75, 0.25, 0.12], color: '#c6cdd5' },
      { on: 'legs', shape: 'cylinder', axis: 'x', at: [1.12, -0.15, 0.15], size: [0.15, 0.42, 0.42], color: '#222326' },
      { on: 'legs', shape: 'cylinder', axis: 'x', at: [1.2, -0.15, 0.15], size: [0.08, 0.2, 0.2], color: '#c6cdd5' },
      { on: 'legs', shape: 'box', at: [0, -0.5, 1.02], size: [0.45, 0.18, 0.06], color: '#1a1e2a', stripes: { color: '#1f4fb5', axis: 'y', period: 0.035 } },
      { on: 'legs', shape: 'box', at: [0, 0.65, 0], size: [1.1, 0.25, 1.1], color: '#c6cdd5' },
    ] },
  megatron: { name: 'Megatron', template: 'humanoid', build: { width: 1.25, height: 1.05 },
    colors: { skin: '#c6cdd5', torso: '#9aa3ad', pelvis: '#5a5f66', arms: '#9aa3ad', hands: '#5a5f66', legs: '#9aa3ad', feet: '#4a4f56' },
    face: { type: 'roboter', expression: 'wuetend', eyes: 'led', eyeColor: '#ff2a2a', mouth: 'gitter', color: '#9aa3ad' },
    add: [
      // bucket helmet around the silver face (robot face from faces.js: angry red eyes, mouth grille), high centre crest, side fins
      { on: 'head', shape: 'box', at: [0, 0.65, -0.08], size: [1.2, 0.45, 1.12], color: '#6f7882' },
      { on: 'head', shape: 'box', at: [0, 0.1, -0.6], size: [1.2, 1.0, 0.55], color: '#6f7882' },
      { on: 'head', shape: 'box', at: [1.02, -0.1, 0.25], size: [0.18, 0.95, 0.8], mirror: true, color: '#6f7882' },
      { on: 'head', shape: 'box', at: [0, 1.2, 0.05], size: [0.24, 0.35, 1.05], color: '#6f7882' },
      { on: 'head', shape: 'box', at: [0, 1.0, 1.0], size: [0.24, 0.5, 0.1], color: '#4a4f56' },
      { on: 'head', shape: 'box', at: [1.2, 0.5, -0.1], size: [0.12, 0.7, 0.6], mirror: true, color: '#4a4f56' },
      // chest: silver plate, glowing purple insignia, abdominal ribs, belt (team)
      { on: 'torso', shape: 'box', at: [0, 0.3, 1.0], size: [0.92, 0.6, 0.08], color: '#c6cdd5' },
      { on: 'torso', shape: 'box', at: [0, 0.42, 1.07], size: [0.3, 0.28, 0.04], color: '#2a1a40' },
      { on: 'torso', shape: 'box', at: [0, 0.42, 1.1], size: [0.2, 0.18, 0.04], color: '#8a3ae0', glow: true },
      { on: 'torso', shape: 'box', at: [0.7, 0.55, 1.06], size: [0.15, 0.25, 0.05], mirror: true, color: '#5a5f66' },
      { on: 'torso', shape: 'box', at: [0, -0.45, 1.02], size: [0.5, 0.3, 0.06], color: '#5a5f66', stripes: { color: '#9aa3ad', axis: 'y', period: 0.035 } },
      { on: 'torso', shape: 'box', at: [0, -0.85, 0], size: [1.05, 0.12, 1.05], color: 'team' },
      { on: 'torso', shape: 'box', at: [0, -0.85, 1.08], size: [0.2, 0.14, 0.05], color: '#c6cdd5' },
      // massive spiked shoulders, forearm armour; signature fusion cannon on the right arm
      { on: 'arms', shape: 'box', at: [0.1, 0.9, 0], size: [1.7, 0.38, 1.55], color: '#6f7882' },
      { on: 'arms', shape: 'cone', at: [0.6, 1.5, 0], size: [0.35, 0.35, 0.35], color: '#c6cdd5' },
      { on: 'arms', shape: 'box', at: [0, -0.4, 0], size: [1.2, 0.3, 1.2], color: '#6f7882' },
      { on: 'armR', shape: 'cylinder', axis: 'z', at: [1.5, -0.45, 1.2], size: [0.95, 0.3, 2.6], color: '#3a3f47' },
      { on: 'armR', shape: 'cylinder', axis: 'z', at: [1.5, -0.45, 0.4], size: [1.1, 0.34, 0.3], color: '#c6cdd5' },
      { on: 'armR', shape: 'cylinder', axis: 'z', at: [1.5, -0.45, 3.75], size: [0.6, 0.18, 0.08], color: '#ff6a2a', glow: true },
      // legs: thigh armour, knee spikes, shin vents, heavy feet
      { on: 'legs', shape: 'box', at: [0, 0.5, 0], size: [1.12, 0.3, 1.12], color: '#6f7882' },
      { on: 'legs', shape: 'box', at: [0, 0.05, 1.05], size: [0.6, 0.18, 0.18], color: '#c6cdd5' },
      { on: 'legs', shape: 'box', at: [0, -0.45, 1.02], size: [0.5, 0.2, 0.06], color: '#3a3f47', stripes: { color: '#9aa3ad', axis: 'y', period: 0.035 } },
      { on: 'legs', shape: 'box', at: [0, -0.86, 0.35], size: [1.25, 0.16, 1.5], color: '#4a4f56' },
    ] },
  pharao: { name: 'Pharao', template: 'humanoid', build: {},
    colors: { skin: '#a8703f', torso: '#a8703f', pelvis: '#f2ecd8', arms: '#a8703f', hands: '#a8703f', legs: '#a8703f', feet: '#c8a050' },
    face: { expression: 'entschlossen', eyes: 'mandel', eyeColor: '#3a2a1a', browColor: '#1a120c', nose: 'spitz', marks: ['kajal'], beard: 'pharao', beardColor: '#2a4fb0', color: '#d9b23c' }, hat: { type: 'nemes', color: '#d9b23c', color2: '#2a4fb0' },
    add: [
      // broad usekh collar, gold armbands, belt (team) with apron, kilt pleats
      { on: 'torso', shape: 'box', at: [0, 0.72, 0], size: [1.14, 0.32, 1.14], color: '#2a4fb0', stripes: { color: '#d9b23c', axis: 'y', period: 0.035 } },
      { on: 'torso', shape: 'box', at: [0, 0.32, 1.02], size: [0.7, 0.1, 0.06], color: '#c8322b', stripes: { color: '#d9b23c', axis: 'x', period: 0.035 } },
      { on: 'torso', shape: 'box', at: [0, 0.1, 1.02], size: [0.15, 0.12, 0.05], color: '#d9b23c' },
      { on: 'pelvis', shape: 'box', at: [0, -1.2, 0.05], size: [1.15, 1.8, 1.12], color: '#f2ecd8', stripes: { color: '#e2dccb', axis: 'x', period: 0.035 } },
      { on: 'pelvis', shape: 'box', at: [0, 0.6, 0], size: [1.1, 0.35, 1.1], color: 'team' },
      { on: 'pelvis', shape: 'box', at: [0, -1.6, 1.15], size: [0.35, 1.6, 0.06], color: '#d9b23c', stripes: { color: '#2a4fb0', axis: 'y', period: 0.066 } },
      { on: 'arms', shape: 'box', at: [0, 0.4, 0], size: [1.1, 0.12, 1.1], color: '#d9b23c' },
      { on: 'arms', shape: 'box', at: [0, -0.55, 0], size: [1.1, 0.14, 1.1], color: '#d9b23c', stripes: { color: '#2a4fb0', axis: 'y', period: 0.035 } },
      // sandals with gold straps, anklets
      { on: 'legs', shape: 'box', at: [0, -0.68, 0], size: [1.06, 0.08, 1.06], color: '#d9b23c' },
      // crook staff in the left hand: blue/gold striped, curled top
      { on: 'armL', shape: 'cylinder', at: [0, 0.1, 1.2], size: [0.22, 4.0, 0.22], color: '#d9b23c', stripes: { color: '#2a4fb0', axis: 'y', period: 0.05 } },
      { on: 'armL', shape: 'cylinder', axis: 'z', at: [0, 4.25, 1.65], size: [0.22, 0.22, 0.6], color: '#d9b23c' },
      { on: 'armL', shape: 'cylinder', at: [0, 3.95, 2.15], size: [0.22, 0.4, 0.22], color: '#2a4fb0' },
    ] },
  seemonster: { name: 'Riesiges Seemonster', template: 'serpent', build: { length: 11, radius: 0.95, segments: 9 },
    colors: { body: '#1f8a8a', belly: '#b8e0c8', head: '#1f8a8a', jaw: '#17706f' },
    face: { type: 'tier', expression: 'wuetend', eyes: 'gluehend', eyeColor: '#ffd23c', muzzle: 'keine', teeth: 'fangzaehne', tongue: 'gespalten' },
    add: [
      // back: team dorsal fin per segment with dark tips, dark spots, side gill slits, striped belly plates
      { on: 'segments', shape: 'box', at: [0, 1.05, 0], size: [0.09, 0.55, 0.7], color: 'team' },
      { on: 'segments', shape: 'box', at: [0, 1.55, -0.1], size: [0.08, 0.12, 0.45], color: '#0f4f4f' },
      { on: 'segments', shape: 'ellipsoid', at: [0.72, 0.45, 0.2], size: [0.22, 0.22, 0.3], mirror: true, color: '#13605f' },
      { on: 'segments', shape: 'ellipsoid', at: [0.5, 0.8, -0.4], size: [0.15, 0.12, 0.18], mirror: true, color: '#7fd6c0' },
      { on: 'segments', shape: 'ellipsoid', at: [0, -0.5, 0], size: [0.7, 0.5, 0.98], color: '#c8ead6', stripes: { color: '#9cc8b0', axis: 'z', period: 0.21 } },
      // tail fluke (team) with dark rim
      { on: 'tail', shape: 'box', at: [0, 0.1, -1.4], size: [1.7, 0.14, 0.85], color: '#0f4f4f' },
      { on: 'tail', shape: 'box', at: [0, 0.1, -1.35], size: [1.55, 0.16, 0.72], color: 'team' },
      // head: rounded back, heavy brow ridges, horns, cheek frills, whiskers (eyes, fangs, tongue from faces.js)
      { on: 'head', shape: 'ellipsoid', at: [0, 0.0, -0.95], size: [1.1, 0.9, 0.5], color: '#1f8a8a' },
      { on: 'head', shape: 'box', at: [0.6, 0.78, 0.35], size: [0.38, 0.12, 0.35], mirror: true, rot: [0, 0, -12], color: '#13605f' },
      { on: 'head', shape: 'cone', at: [0.55, 1.1, -0.45], size: [0.18, 0.6, 0.18], rot: [-35, 0, 15], mirror: true, color: '#eadfc4' },
      { on: 'head', shape: 'cone', at: [0, 1.0, -0.2], size: [0.12, 0.35, 0.12], rot: [-30, 0, 0], color: '#eadfc4' },
      { on: 'head', shape: 'box', at: [1.0, 0.0, -0.6], size: [0.1, 0.55, 0.4], mirror: true, rot: [0, -20, 0], color: 'team' },
      { on: 'head', shape: 'cylinder', axis: 'x', at: [1.05, -0.4, 0.75], size: [0.45, 0.05, 0.05], mirror: true, rot: [0, 0, -25], color: '#7fd6c0' },
      // mouth lining and lower teeth
      { on: 'jaw', shape: 'box', at: [0, 0.75, 0.1], size: [0.75, 0.2, 0.85], color: '#6a1a2a' },
      { on: 'jaw', shape: 'box', at: [0.7, 1.05, 0.35], size: [0.1, 0.25, 0.6], mirror: true, color: '#f2efe6', stripes: { color: '#17706f', axis: 'z', period: 0.1 } },
    ] },
};
const RPROPS = {
  chibi: { legX: 0.13, legH: 0.6, legHX: 0.12, legHZ: 0.13, pelHY: 0.07, torHX: 0.28, torHY: 0.27, torHZ: 0.16, armHX: 0.1, armHY: 0.29, armHZ: 0.1, head: [0.27, 0.26, 0.25] },
  real: { legX: 0.105, legH: 0.9, legHX: 0.09, legHZ: 0.1, pelHY: 0.08, torHX: 0.22, torHY: 0.27, torHZ: 0.13, armHX: 0.07, armHY: 0.34, armHZ: 0.07, head: [0.11, 0.13, 0.12] },
};
function rProps(t) {
  const a = RPROPS.chibi, b = RPROPS.real, o = {};
  for (const k in a) o[k] = Array.isArray(a[k]) ? a[k].map((v, i) => v + (b[k][i] - v) * t) : a[k] + (b[k] - a[k]) * t;
  return o;
}
const headsTall = t => { const P = rProps(t); return (P.legH + 2 * P.pelHY + 2 * P.torHY + 2 * P.head[1]) / (2 * P.head[1]); };
const rHex = h => h === 'team' ? T : h === 'team2' ? T2 : parseInt(h.slice(1), 16);
function rPrim(frame, a, part, mirror) {
  const at = a.at || [0, 0, 0], size = a.size || [1, 1, 1], mx = mirror ? -1 : 1;
  const c = [frame.c[0] + mx * at[0] * frame.h[0], frame.c[1] + at[1] * frame.h[1], frame.c[2] + at[2] * frame.h[2]];
  const h = [size[0] * frame.h[0], size[1] * frame.h[1], size[2] * frame.h[2]];
  let inv = null;
  if (a.rot) {
    const r = a.rot.map(d => d * Math.PI / 180);
    inv = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(r[0], mirror ? -r[1] : r[1], mirror ? -r[2] : r[2])).invert().elements;
  }
  const cs = typeof a.color === 'string' ? { color: a.color } : (a.color || { color: '#ff00ff' });
  const st = a.stripes || (cs.stripes ? { color: cs.stripes, axis: 'y', period: 0.05 } : null);
  return { shape: a.shape || 'box', axis: a.axis || 'y', c, h, inv, part, color: rHex(cs.color), glow: !!a.glow,
    stripes: st ? { color: rHex(st.color), axis: 'xyz'.indexOf(st.axis || 'y'), period: st.period || 0.05 } : null,
    checker: a.checker ? rHex(a.checker) : (cs.checker ? rHex(cs.checker) : null) };
}
function compileRecipe(rec, t, b) {
  if (rec.template === 'quadruped') return compileQuadruped(rec, t, b);
  const prims = [], parts = [];
  if (rec.template === 'serpent') {
    const L = b.length || 10, r = b.radius || 0.8, n = Math.min(9, b.segments || 8), seg = L / n, col = rec.colors || {};
    const segF = [];
    for (let k = 0; k < n; k++) {
      const u = k / (n - 1), rk = r * (1 - 0.55 * u);
      const y = r + Math.pow(1 - u, 2.2) * r * 3.6 + Math.max(0, Math.sin(u * Math.PI * 2.2)) * r * 0.9 * u;
      segF.push({ c: [0, y, L / 2 - seg * (k + 0.5)], h: [rk, rk, seg * 0.64] });
    }
    const hy = segF[0].c[1] + r * 0.55, hz = L / 2 + r * 0.85;
    const head = { c: [0, hy, hz], h: [r * 0.95, r * 0.72, r * 1.15] };
    const jaw = { c: [0, hy - r * 0.78, hz + r * 0.12], h: [r * 0.82, r * 0.26, r * 1.0] };
    parts.push({ role: ROLE.ROOT, pivot: [0, 0, 0] }, { role: ROLE.HEAD, pivot: [0, hy, L / 2] }, { role: ROLE.JAW, pivot: [0, hy - r * 0.6, hz - r * 0.75] });
    for (let k = 0; k < n; k++) parts.push({ role: ROLE.WAVE, pivot: [0, segF[k].c[1], segF[k].c[2] + seg / 2], base: k });
    prims.push(Object.assign(rPrim(head, { shape: 'ellipsoid', color: col.head || col.body }, 1, false), { hf: true, face: true }));
    prims.push(Object.assign(rPrim(jaw, { shape: 'box', color: col.jaw || col.body }, 2, false), { hf: true, face: true }));
    segF.forEach((f, k) => {
      prims.push(rPrim(f, { shape: 'ellipsoid', color: col.body }, 3 + k, false));
      prims.push(rPrim(f, { shape: 'ellipsoid', at: [0, -0.3, 0], size: [0.82, 0.72, 0.98], color: col.belly || col.body }, 3 + k, false));
    });
    // face, hair and headgear (faces.js / hair.js); legacy face boxes are replaced by the painted face
    const hd = faceModeOf(rec) === 'aus' ? { before: [], after: [], add: rec.add || [] } : headPrims(rec, { kind: 'serpent', head, jaw, headPart: 1, jawPart: 2 }, rPrim);
    prims.push(...hd.before);
    for (const a of hd.add) {
      const fr = a.on === 'segments' ? segF.map((f, k) => [f, 3 + k]) : a.on === 'tail' ? [[segF[n - 1], 3 + n - 1]] : a.on === 'jaw' ? [[jaw, 2]] : [[head, 1]];
      const hf = a.on === 'head' || a.on === 'jaw';
      for (const [f, pi] of fr) { prims.push(Object.assign(rPrim(f, a, pi, false), hf ? { hf } : {})); if (a.mirror) prims.push(Object.assign(rPrim(f, a, pi, true), hf ? { hf } : {})); }
    }
    prims.push(...hd.after);
    return { prims, parts, mouth: { y: hy - r * 0.3, z: hz + r * 1.1 }, r, head, headParts: [1, 2] };
  }
  const P = rProps(t);
  const w = b.width || 1, hs = b.headScale || 1, tall = b.height || 1, sw = Math.sqrt(w);
  const legH = P.legH * tall, torHY = P.torHY * tall, armHY = P.armHY * tall;
  const pelC = legH + P.pelHY, torC = legH + 2 * P.pelHY + torHY, torTop = torC + torHY, tx = P.torHX * w;
  const ax = tx + P.armHX * sw + 0.004;
  const F = {
    legR: { c: [P.legX * w, legH / 2, 0], h: [P.legHX * sw, legH / 2, P.legHZ] },
    legL: { c: [-P.legX * w, legH / 2, 0], h: [P.legHX * sw, legH / 2, P.legHZ] },
    pelvis: { c: [0, pelC, 0], h: [tx * 0.95, P.pelHY, P.torHZ * 0.95 * sw] },
    torso: { c: [0, torC, 0], h: [tx, torHY, P.torHZ * sw] },
    armR: { c: [ax, torTop - armHY, 0], h: [P.armHX * sw, armHY, P.armHZ * sw] },
    armL: { c: [-ax, torTop - armHY, 0], h: [P.armHX * sw, armHY, P.armHZ * sw] },
    head: { c: [0, torTop + P.head[1] * hs, 0], h: P.head.map(v => v * hs) },
  };
  const pIdx = { torso: 0, pelvis: 0, head: 1, armL: 2, armR: 3, legL: 4, legR: 5 };
  const armBase = b.armForward ? -1.45 : 0;
  parts.push({ role: ROLE.ROOT, pivot: [0, 0, 0] }, { role: ROLE.HEAD, pivot: [0, torTop, 0] },
    { role: ROLE.ARML, pivot: [-ax, torTop - P.armHX, 0], base: armBase }, { role: ROLE.ARMR, pivot: [ax, torTop - P.armHX, 0], base: armBase },
    { role: ROLE.LEGL, pivot: [-P.legX * w, legH, 0] }, { role: ROLE.LEGR, pivot: [P.legX * w, legH, 0] });
  const col = rec.colors || {};
  const cc = (k, d) => col[k] || col[d] || '#888888';
  const base = (on, a) => prims.push(rPrim(F[on], a, pIdx[on], false));
  for (const sd of ['L', 'R']) {
    base('leg' + sd, { color: cc('legs') });
    base('leg' + sd, { at: [0, -0.88, 0.28], size: [1.08, 0.14, 1.32], color: cc('feet', 'legs') });
    base('arm' + sd, { color: cc('arms', 'torso') });
    base('arm' + sd, { at: [0, -0.86, 0], size: [1.06, 0.16, 1.06], color: cc('hands', 'skin') });
  }
  base('pelvis', { color: cc('pelvis', 'legs') }); base('torso', { color: cc('torso') }); base('head', { color: cc('skin') });
  Object.assign(prims[prims.length - 1], { hf: true, face: true });
  // face, hair and headgear (faces.js / hair.js); legacy face boxes are replaced by the painted face
  const hd = faceModeOf(rec) === 'aus' ? { before: [], after: [], add: rec.add || [] } : headPrims(rec, { kind: 'humanoid', head: F.head, jaw: null, headPart: 1, jawPart: -1 }, rPrim);
  prims.push(...hd.before);
  for (const a of hd.add) {
    const targets = a.on === 'arms' ? ['armR', 'armL'] : a.on === 'legs' ? ['legR', 'legL'] : [a.on];
    targets.forEach((tg, i) => {
      if (!F[tg]) return;
      const pair = i === 1, hf = tg === 'head';
      prims.push(Object.assign(rPrim(F[tg], a, pIdx[tg], pair), hf ? { hf } : {}));
      if (a.mirror && !pair) prims.push(Object.assign(rPrim(F[tg], a, pIdx[tg], true), hf ? { hf } : {}));
    });
  }
  prims.push(...hd.after);
  const A = F.armR;
  return { prims, parts, head: F.head, hand: { x: A.c[0], y: A.c[1] - 0.86 * A.h[1], z: A.c[2], front: A.c[2] + A.h[2], bottom: A.c[1] - 1.02 * A.h[1] } };
}
/* fine head layer settings for a compiled recipe (null = single layer). o.detail > 1 asks for the close-up LOD. */
function headFine(comp, vs, o, fm = 'fein') {
  if (o.paint === false) return { f: 1, region: null, paint: false };
  if (!(o.detail > 1) || !comp.head || o.fineHead === false || fm !== 'fein') return null;
  const f = fineFactor(comp.head.h[0], vs);
  if (f < 2) return null;
  const H = comp.head;
  const cap = { lo: [H.c[0] - H.h[0] * 2.6, H.c[1] - H.h[1] * 3.4, H.c[2] - H.h[2] * 2.6], hi: [H.c[0] + H.h[0] * 2.6, H.c[1] + H.h[1] * 3.2, H.c[2] + H.h[2] * 3.2] };
  return { f, region: headRegion(comp.prims, vs, cap), parts: comp.headParts || [1] };
}
function recipeModel(key, o = {}) {
  const rec = typeof key === 'string' ? RECIPES[key] : key;
  if (rec.template === 'quadruped') return quadrupedModel(rec, o);
  const t = o.t ?? 0.4;
  const b = { ...(rec.build || {}) };
  b.width = (b.width || 1) * (o.width || 1); b.headScale = (b.headScale || 1) * (o.head || 1);
  const comp = compileRecipe(rec, t, b);
  const serp = rec.template === 'serpent';
  // detail 2 = half voxel size (close-up LOD); weapon art and mount offsets scale with it
  const k = o.detail > 1 ? 2 : 1;
  const vs = (serp ? 0.14 : (1.8 / 22) / (1 + t * 0.6)) / k;
  const m = new VoxModel(vs);
  m.detail = k;
  // close-up LOD: the head region gets its own finer voxel layer (heads.js) so faces have ~20-26 voxels across
  rasterize(m, comp.prims, vs, headFine(comp, vs, o, faceModeOf(rec)));
  m.parts = comp.parts.map(pp => ({ role: pp.role, pivot: pp.pivot.map(v => v / vs), base: pp.base || 0 }));
  if (serp) {
    m.kind = 'serpent'; m.atkStyle = 1; m.waveAmp = comp.r * 0.45;
    m.mountMelee = m.mountRanged = { part: 1, a: [-k, Math.round(comp.mouth.y / vs), Math.floor(comp.mouth.z / vs) + k], f: [0, 0, 1], u: [0, 1, 0], s: [1, 0, 0] };
  } else {
    m.kind = 'humanoid'; m.atkStyle = 0; m.p = { pArmL: 2, pArmR: 3 };
    const H = comp.hand, axv = Math.round(H.x / vs - k);
    m.mountMelee = { part: 3, a: [axv, Math.round(H.y / vs - k), Math.floor(H.front / vs) + k], f: [0, 0, 1], u: [0, 1, 0], s: [1, 0, 0] };
    m.mountRanged = { part: 3, a: [axv, Math.floor(H.bottom / vs) - k, Math.round(H.z / vs - k)], f: [0, -1, 0], u: [0, 0, 1], s: [1, 0, 0] };
  }
  return m;
}

/* ---------------- magic wand: recipe mutations (the LLM will do this in the real game) ---------------- */
const lum = hex => { const v = parseInt(hex.slice(1), 16); return (((v >> 16) & 255) * 0.3 + ((v >> 8) & 255) * 0.59 + (v & 255) * 0.11) / 255; };
const hx = n => '#' + n.toString(16).padStart(6, '0');
function mapColors(rec, fn) {
  const r = JSON.parse(JSON.stringify(rec));
  const mc = c => (typeof c === 'string' && c[0] === '#') ? fn(c) : c;
  const mo = c => typeof c === 'string' ? mc(c) : c && typeof c === 'object' ? { ...c, color: mc(c.color), checker: c.checker ? mc(c.checker) : undefined, stripes: c.stripes ? mc(c.stripes) : undefined } : c;
  for (const k in r.colors) r.colors[k] = mo(r.colors[k]);
  for (const a of r.add || []) { a.color = mo(a.color); if (a.stripes) a.stripes = { ...a.stripes, color: mc(a.stripes.color) }; if (a.checker) a.checker = mc(a.checker); }
  // face painter colours (faces.js / hair.js); unset ones follow the mapped skin/hair automatically
  if (r.face) for (const k of ['eyeColor', 'browColor', 'lipColor', 'beardColor', 'glassesColor', 'color', 'hornColor']) if (r.face[k]) r.face[k] = mc(r.face[k]);
  if (r.hair && r.hair.color) r.hair.color = mc(r.hair.color);
  if (r.hat) for (const k of ['color', 'color2']) if (r.hat[k]) r.hat[k] = mc(r.hat[k]);
  return r;
}
// wand variants change the face through the face painter: keep the recipe's face options, override a few
const withFace = (o, face, hair, hat) => {
  o.face = { ...(o.face || {}), ...face };
  if (hair !== undefined) o.hair = hair;
  if (hat !== undefined) o.hat = hat;
  return o;
};
const ramp = (cols) => c => cols[Math.min(cols.length - 1, Math.floor(lum(c) * cols.length))];
const VARIANTS = {
  clown: { name: 'Clown', desc: 'Regenbogen-Afro, rote Nase, riesige Schuhe.', fn: r => {
    const o = mapColors(r, c => ['#e83e8c', '#ffd23c', '#3ad1ff', '#7cff4f'][Math.floor(lum(c) * 7) % 4]);
    if (o.template === 'humanoid') {
      o.colors.skin = '#f7ece4'; o.colors.torso = { color: '#e83e8c', checker: '#ffd23c' };
      withFace(o, { type: 'mensch', expression: 'irre', nose: 'clown', mouth: 'grinsen', marks: ['clown'], beard: 'keiner', glasses: 'keine' }, { style: 'afro', color: '#ff4fd8' }, null);
      delete o.hat;
      o.add.push({ on: 'head', shape: 'ellipsoid', at: [1.15, 0.45, -0.1], size: [0.5, 0.6, 0.8], mirror: true, color: '#3ad1ff' },
        { on: 'torso', shape: 'box', at: [0, 1.0, 0], size: [1.35, 0.16, 1.35], color: '#ffffff', checker: '#ff4fd8' },
        { on: 'legs', shape: 'box', at: [0, -0.9, 0.7], size: [1.45, 0.2, 2.1], color: '#d0202a' });
    }
    return o; } },
  robo: { name: 'Robo', desc: 'Metallkörper, Visier-Laser, Antenne.', fn: r => {
    const o = mapColors(r, ramp(['#3a3f47', '#5a616b', '#7a828c', '#9aa3ad', '#c6cdd5']));
    if (o.template === 'humanoid') {
      withFace(o, { type: 'roboter', eyes: 'visier', eyeColor: '#3af0ff', mouth: 'gitter', beard: 'keiner', marks: [] }, { style: 'keine' });
      o.add.push({ on: 'head', shape: 'box', at: [0.6, 1.3, 0], size: [0.08, 0.5, 0.08], color: '#5a616b' },
        { on: 'head', shape: 'ellipsoid', at: [0.6, 1.85, 0], size: [0.16, 0.16, 0.16], color: '#ff3b3b', glow: true },
        { on: 'torso', shape: 'box', at: [0, 0.3, 1.03], size: [0.5, 0.35, 0.05], color: '#2b2e33' },
        { on: 'torso', shape: 'box', at: [-0.18, 0.3, 1.07], size: [0.08, 0.08, 0.04], color: '#7cff4f', glow: true });
    } else o.add.push({ on: 'segments', shape: 'box', at: [0, 0.9, 0.5], size: [0.15, 0.15, 0.15], color: '#3af0ff', glow: true });
    return o; } },
  zombie: { name: 'Zombie', desc: 'Grüne Haut, zerfetzte Kleidung, glühende Augen.', fn: r => {
    const o = mapColors(r, c => { const v = parseInt(c.slice(1), 16); const k = 0.62; return hx((((v >> 16) & 255) * k | 0) << 16 | (((v >> 8) & 255) * k | 0) << 8 | ((v & 255) * k | 0)); });
    if (o.template === 'humanoid') {
      o.colors.skin = '#7fa65a'; o.colors.hands = '#7fa65a'; o.build = { ...o.build, armForward: true };
      if (o.face && o.face.type === 'keins') delete o.face.type;
      withFace(o, { expression: 'irre', eyes: 'gluehend', eyeColor: '#ff4a2a', mouth: 'schreien', marks: ['naht', 'dreck'] });
      o.add.push({ on: 'torso', shape: 'box', at: [0.4, -0.3, 1.0], size: [0.3, 0.25, 0.08], color: '#7fa65a' });
    } else o.colors.body = '#4f7a3a';
    return o; } },
  gold: { name: 'Goldene Statue', desc: 'Komplett aus glänzendem Gold.', fn: r => mapColors(r, ramp(['#8a6a1a', '#b8901f', '#d9b23c', '#f2d06b'])) },
  ninja: { name: 'Ninja', desc: 'Schwarzer Anzug, Maske, Stirnband.', fn: r => {
    const o = mapColors(r, c => lum(c) > 0.55 ? '#2a2a33' : '#16161b');
    if (o.template === 'humanoid') {
      o.colors.skin = '#e3b48a';
      withFace(o, { type: 'mensch', expression: 'entschlossen', eyes: 'schmal', brows: 'dick', beard: 'keiner', glasses: 'keine', marks: [] }, { style: 'keine' }, { type: 'ninjamaske', color: '#16161b', color2: 'team' });
    }
    return o; } },
  pirat: { name: 'Pirat', desc: 'Dreispitz, Augenklappe, Ringelhemd.', fn: r => {
    const o = JSON.parse(JSON.stringify(r));
    if (o.template === 'humanoid') {
      o.colors.torso = { color: '#f4f4f2', stripes: '#c8102e' };
      withFace(o, { glasses: 'augenklappe', beard: 'vollbart', beardColor: '#5a3a22', expression: 'boese', marks: ['narbe_wange'] }, o.hair || { style: 'lang', color: '#3a2416' }, { type: 'dreispitz', color: '#1a1a1f', color2: '#d9b23c' });
    } else o.colors.body = '#3a2a22';
    return o; } },
  wikinger: { name: 'Wikinger', desc: 'Hörnerhelm, roter Bart, Fellkragen.', fn: r => {
    const o = JSON.parse(JSON.stringify(r));
    if (o.template === 'humanoid') {
      withFace(o, { beard: 'langer_bart', beardColor: '#c4521e', expression: 'wuetend', brows: 'buschig', browColor: '#c4521e' }, { style: 'lang', color: '#c4521e' }, { type: 'helm_wikinger', color: '#7a828c', color2: '#eadfc4' });
      o.add.push({ on: 'torso', shape: 'box', at: [0, 0.85, 0], size: [1.3, 0.3, 1.3], color: '#8a6a4a', checker: '#6b5038' });
    } else o.add.push({ on: 'head', shape: 'cone', at: [0.5, 1.2, 0], size: [0.2, 0.7, 0.2], rot: [0, 0, -30], mirror: true, color: '#eadfc4' });
    return o; } },
  muskel: { name: 'Muskelprotz', desc: 'Doppelt so breit, winziger Kopf.', fn: r => {
    const o = JSON.parse(JSON.stringify(r)); o.build = { ...o.build, width: (o.build?.width || 1) * 1.55, headScale: (o.build?.headScale || 1) * 0.75 }; return o; } },
  feuer: { name: 'Feuer', desc: 'Glühend heiß, in Flammenfarben.', fn: r => {
    const o = mapColors(r, ramp(['#5a1a0a', '#b8341a', '#f0702a', '#ffc24a']));
    o.add.push(o.template === 'humanoid' ? { on: 'head', shape: 'ellipsoid', at: [0, 1.1, 0], size: [0.9, 0.6, 0.9], color: '#ffb340', glow: true } : { on: 'segments', shape: 'box', at: [0, 1.1, 0], size: [0.1, 0.5, 0.5], color: '#ffb340', glow: true });
    return o; } },
};
let mutantN = 0;
function registerMutant(baseType, vKey) {
  const B = TYPES[baseType], V = VARIANTS[vKey];
  const rk = 'mut' + (++mutantN);
  RECIPES[rk] = V.fn(RECIPES[B.recipe]);
  const label = `${V.name}-${B.name}`;
  const tk = 'u_' + rk;
  TYPES[tk] = { ...B, name: label, plural: label, recipe: rk };
  return tk;
}

/* ---------------- weapons ---------------- */
const WEAPONS = {
  faeuste: { name: 'Fäuste / Biss', melee: true, reach: 0.55, dmg: 10, cd: 0.8, knock: 0.6, atkDur: 0.55 },
  schwert: { name: 'Schwert', melee: true, reach: 1.15, dmg: 24, cd: 0.9, knock: 1.2, atkDur: 0.6,
    vox: (put) => { put(0, 0, -1, 2, 0, 1, GOLD); put(1, 11, 0, 1, 0, 1, (f, u) => u === 1 ? STEEL_L : 0xdfe5ec); put(12, 12, 0, 1, 0, 0, STEEL_L); } },
  plasma: { name: 'Plasmaklinge', melee: true, reach: 1.7, dmg: 70, cd: 0.6, knock: 5, cleave: true, atkDur: 0.5,
    vox: (put) => { put(-1, 1, 0, 1, 0, 1, 0x3a3f47); put(2, 2, -1, 2, -1, 2, 0xa9b2bd); put(3, 18, 0, 1, 0, 1, P, true); } },
  gewehr: { name: 'Gewehr', melee: false, hitscan: true, range: 28, dmg: 16, cd: 1.1, acc: 0.7, knock: 1, atkDur: 0.25,
    vox: (put) => { put(0, 6, 0, 1, 0, 1, DARK); put(1, 4, 0, 1, -1, -1, 0x7a5532); put(7, 11, 0, 1, 1, 1, 0x5b6066); put(2, 3, 0, 1, -3, -2, DARK); }, tip: [12, 1, 0.5] },
  minigun: { name: 'Minigun', melee: false, hitscan: true, range: 22, dmg: 6, cd: 0.11, acc: 0.55, knock: 0.6, atkDur: 0.12,
    vox: (put) => { put(0, 3, -1, 2, -1, 2, DARK); put(4, 12, 0, 1, 0, 1, 0x8d939a); put(8, 8, -1, 2, -1, 2, DARK); }, tip: [13, 0.5, 0.5] },
  raketen: { name: 'Raketenwerfer', melee: false, proj: 0, pspeed: 24, range: 34, dmg: 55, cd: 2.4, aoe: 3.2, knock: 7, atkDur: 0.35,
    vox: (put) => { put(-2, 9, -1, 2, -1, 2, 0x556b2f); put(10, 10, -1, 2, -1, 2, DARK); put(10, 10, 0, 1, 0, 1, 0xc0392b); put(2, 4, 0, 1, 3, 3, DARK); }, tip: [11, 0.5, 0.5] },
  kanone: { name: 'Panzerkanone', melee: false, proj: 1, pspeed: 65, range: 44, dmg: 140, cd: 3.2, aoe: 4, knock: 10, atkDur: 0.45,
    vox: (put) => { put(0, 11, 0, 1, 0, 1, 0x4a5530); put(12, 13, -1, 2, -1, 2, 0x30361f); }, tip: [14, 0.5, 0.5] },
  huhnkanone: { name: 'Gummihuhn-Kanone', melee: false, proj: 2, pspeed: 15, range: 26, dmg: 40, cd: 1.5, aoe: 3, knock: 7, arc: true, atkDur: 0.4,
    vox: (put) => { put(0, 6, -1, 2, -1, 2, 0xffd23c); put(7, 7, -1, 2, -1, 2, 0xe74c3c); put(7, 7, 0, 1, 0, 1, 0x111111); }, tip: [8, 0.5, 0.5] },
};

/* ---------------- unit types ---------------- */
const TYPES = {
  ritter: { name: 'Ritter', plural: 'Ritter', recipe: 'ritter', bravery: 0.45, hp: 130, speed: 3.2, r: 0.52, mass: 1.2, dmgMul: 1, weapon: 'schwert', debris: 26 },
  legionaer: { name: 'Legionär', plural: 'Legionäre', recipe: 'legionaer', bravery: 0.55, hp: 120, speed: 3.4, r: 0.52, mass: 1.1, dmgMul: 1.1, weapon: 'schwert', debris: 26 },
  soldat: { name: 'Soldat', plural: 'Soldaten', recipe: 'soldat', bravery: 0.3, hp: 95, speed: 3.6, r: 0.45, mass: 1, dmgMul: 1, weapon: 'gewehr', debris: 22 },
  zombie: { name: 'Zombie', plural: 'Zombies', recipe: 'zombie', bravery: 9, hp: 80, speed: 1.9, r: 0.45, mass: 1, dmgMul: 1, weapon: 'faeuste', debris: 22 },
  priester: { name: 'Hohepriester', plural: 'Hohepriester', recipe: 'priester', bravery: 0.9, hp: 150, speed: 3, r: 0.55, mass: 1.2, dmgMul: 1, weapon: 'faeuste', debris: 26 },
  trump: { name: 'Donald Trump', plural: 'Trumps', recipe: 'trump', bravery: 0.6, hp: 170, speed: 3, r: 0.55, mass: 1.4, dmgMul: 1.2, weapon: 'faeuste', debris: 26 },
  megamind: { name: 'Megamind', plural: 'Megaminds', recipe: 'megamind', bravery: 0.5, hp: 120, speed: 3.4, r: 0.5, mass: 1, dmgMul: 1, weapon: 'gewehr', debris: 24 },
  optimus: { name: 'Optimus Prime', plural: 'Optimus Primes', recipe: 'optimus', bravery: 9, hp: 1400, speed: 3.8, r: 0.75, mass: 5, dmgMul: 3, weapon: 'plasma', debris: 40 },
  megatron: { name: 'Megatron', plural: 'Megatrons', recipe: 'megatron', bravery: 9, hp: 1500, speed: 3.4, r: 0.75, mass: 5, dmgMul: 2.5, weapon: 'kanone', debris: 40 },
  pharao: { name: 'Pharao', plural: 'Pharaonen', recipe: 'pharao', bravery: 0.7, hp: 180, speed: 3, r: 0.5, mass: 1.1, dmgMul: 1.2, weapon: 'schwert', debris: 26 },
  seemonster: { name: 'Riesiges Seemonster', plural: 'Seemonster', recipe: 'seemonster', bravery: 9, hp: 40000, speed: 3.2, r: 3.2, mass: 60, dmgMul: 25, stomp: true, weapon: 'faeuste', debris: 140 },
  huhn: { name: 'Huhn', plural: 'Hühner', bravery: 0.05, hp: 14, speed: 4.6, r: 0.24, mass: 0.25, dmgMul: 0.3, weapon: 'faeuste', debris: 10 },
  mechhase: { name: 'Mech-Hase', plural: 'Mech-Hasen', bravery: 9, hp: 650, speed: 3.4, r: 0.95, mass: 4, dmgMul: 2.2, weapon: 'huhnkanone', debris: 40 },
  panzer: { name: 'Panzer', plural: 'Panzer', bravery: 9, hp: 1600, speed: 2.6, r: 1.85, mass: 6, dmgMul: 1, weapon: 'kanone', debris: 55 },
  trex: { name: 'T-Rex', plural: 'T-Rex', bravery: 9, hp: 3200, speed: 4.2, r: 1.7, mass: 14, dmgMul: 9, stomp: true, weapon: 'faeuste', debris: 80 },
};

const PRESETS = [
  { name: 'Hohepriester mit Plasmaklinge vs. 1.000 Panzer', a: ['priester', 1, 'plasma', 12], b: ['panzer', 1000, 'kanone', 1] },
  { name: '10.000 Hühner vs. 1 T-Rex', a: ['huhn', 10000, 'faeuste', 1], b: ['trex', 1, 'faeuste', 5] },
  { name: 'Ritter vs. Legionäre', a: ['ritter', 1200, 'schwert', 1], b: ['legionaer', 1200, 'schwert', 1] },
  { name: 'Zombie-Flut', a: ['soldat', 800, 'gewehr', 1], b: ['zombie', 6000, 'faeuste', 1] },
  { name: 'Mech-Hasen vs. Panzer', a: ['mechhase', 220, 'huhnkanone', 1], b: ['panzer', 60, 'kanone', 1] },
  { name: 'Trump vs. Megamind', a: ['trump', 400, 'faeuste', 1], b: ['megamind', 400, 'gewehr', 1] },
  { name: 'Optimus Prime vs. Megatron', a: ['optimus', 1, 'plasma', 5, 55], b: ['megatron', 1, 'kanone', 5, 55] },
  { name: 'Pharaonen vs. Seemonster', a: ['pharao', 1000, 'schwert', 1], b: ['seemonster', 1, 'faeuste', 1] },
];

/* ---------------- meshing ---------------- */
const FACE = [0.86, 0.86, 1.0, 0.62, 0.93, 0.8]; // +x -x +y -y +z -z
const AOF = [0.5, 0.68, 0.84, 1.0];
const tmpC = new THREE.Color();
function teamColor(c, team, out) {
  const t = TEAMS[team];
  const hex = c === T ? t.main : c === T2 ? t.dark : c === TL ? t.light : c === P ? t.plasma : c;
  return out.setHex(hex);
}
function modelCenter(m) {
  let mnx = 1e9, mxx = -1e9, mnz = 1e9, mxz = -1e9, mny = 1e9, mxy = -1e9;
  for (const [k, v] of m.map) {
    const [x, y, z] = DEC(k);
    if (y < mny) mny = y; if (y + 1 > mxy) mxy = y + 1;
    if (v.p !== 0) continue;
    if (x < mnx) mnx = x; if (x + 1 > mxx) mxx = x + 1; if (z < mnz) mnz = z; if (z + 1 > mxz) mxz = z + 1;
  }
  if (mnx > mxx) { for (const k of m.map.keys()) { const [x, y, z] = DEC(k); if (x < mnx) mnx = x; if (x + 1 > mxx) mxx = x + 1; if (z < mnz) mnz = z; if (z + 1 > mxz) mxz = z + 1; } }
  const fb = m.fine && fineBounds(m); if (fb) { mny = Math.min(mny, fb.y0); mxy = Math.max(mxy, fb.y1); } // fine head layer (heads.js)
  return { cx: (mnx + mxx) / 2, cz: (mnz + mxz) / 2, y0: mny, h: mxy - mny };
}
function finishGeo(pos, nor, col, part, emit, idx) {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('aPart', new THREE.Float32BufferAttribute(part, 1));
  g.setAttribute('aEmit', new THREE.Float32BufferAttribute(emit, 1));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}
function satBoost(c, sat) { if (sat === 1) return c; const hsl = {}; c.getHSL(hsl); return c.setHSL(hsl.h, Math.min(1, hsl.s * sat), hsl.l); }
function meshAO(m, team, ctr, jitter = 0.07, useAO = true, sat = 1) {
  if (m.fine && m.fine.map.size) return meshWithFine(m, team, ctr, meshAO, [jitter, useAO, sat]); // fine head layer (heads.js)
  const vs = m.vs, map = m.map, { cx, cz, y0 } = ctr;
  const pos = [], nor = [], col = [], part = [], emit = [], idx = [];
  const has = (x, y, z) => map.has(KEY(x, y, z));
  const cs = [[0, 0], [1, 0], [1, 1], [0, 1]];
  const b = [0, 0, 0], L = [0, 0, 0], q = [0, 0, 0];
  for (const [k, v] of map) {
    const [x, y, z] = DEC(k);
    satBoost(teamColor(v.c, team, tmpC), sat);
    const j = v.e ? 1 : 1 + (hash3(x, y, z) - 0.5) * jitter * 2;
    b[0] = x; b[1] = y; b[2] = z;
    for (let d = 0; d < 6; d++) {
      const a = d >> 1, s = (d & 1) ? -1 : 1;
      L[0] = x; L[1] = y; L[2] = z; L[a] += s;
      if (has(L[0], L[1], L[2])) continue;
      const u = (a + 1) % 3, w = (a + 2) % 3;
      const start = pos.length / 3; const aos = [];
      for (const c of cs) {
        const du = c[0] ? 1 : -1, dw = c[1] ? 1 : -1;
        q[0] = L[0]; q[1] = L[1]; q[2] = L[2]; q[u] += du; const o1 = has(q[0], q[1], q[2]) ? 1 : 0;
        q[u] -= du; q[w] += dw; const o2 = has(q[0], q[1], q[2]) ? 1 : 0;
        q[u] += du; const o3 = has(q[0], q[1], q[2]) ? 1 : 0;
        const ao = !useAO ? 3 : (o1 && o2) ? 0 : 3 - (o1 + o2 + o3);
        aos.push(ao);
        const p = [0, 0, 0]; p[a] = b[a] + (s > 0 ? 1 : 0); p[u] = b[u] + c[0]; p[w] = b[w] + c[1];
        pos.push((p[0] - cx) * vs, (p[1] - y0) * vs, (p[2] - cz) * vs);
        const n = [0, 0, 0]; n[a] = s; nor.push(n[0], n[1], n[2]);
        const f = (v.e ? 1 : AOF[ao] * FACE[d]) * j;
        col.push(tmpC.r * f, tmpC.g * f, tmpC.b * f);
        part.push(v.p); emit.push(v.e);
      }
      const flip = aos[0] + aos[2] < aos[1] + aos[3];
      if (s > 0) { if (!flip) idx.push(start, start + 1, start + 2, start, start + 2, start + 3); else idx.push(start + 1, start + 2, start + 3, start + 1, start + 3, start); }
      else { if (!flip) idx.push(start, start + 2, start + 1, start, start + 3, start + 2); else idx.push(start + 1, start + 3, start + 2, start + 1, start, start + 3); }
    }
  }
  return finishGeo(pos, nor, col, part, emit, idx);
}
function meshGreedy(m, team, ctr, f = 2, sat = 1, shade = 0.92) {
  if (f === 1 && m.fine && m.fine.map.size) return meshWithFine(m, team, ctr, meshGreedy, [f, sat, shade]); // fine head layer (heads.js), near LOD
  const vs = m.vs, { cx, cz, y0 } = ctr;
  let mn = [1e9, 1e9, 1e9], mx = [-1e9, -1e9, -1e9];
  for (const k of m.map.keys()) { const p = DEC(k); for (let a = 0; a < 3; a++) { if (p[a] < mn[a]) mn[a] = p[a]; if (p[a] > mx[a]) mx[a] = p[a]; } }
  const dims = [0, 1, 2].map(a => Math.ceil((mx[a] - mn[a] + 1) / f));
  const cells = new Map(); const combos = new Map(); const info = [];
  for (const [k, v] of m.map) {
    const p = DEC(k);
    const ci = ((p[0] - mn[0]) / f | 0) + dims[0] * (((p[1] - mn[1]) / f | 0) + dims[1] * ((p[2] - mn[2]) / f | 0));
    const ck = v.c + '|' + v.p + '|' + v.e;
    let id = combos.get(ck); if (id === undefined) { id = info.length + 1; combos.set(ck, id); info.push(v); }
    let cm = cells.get(ci); if (!cm) { cm = new Map(); cells.set(ci, cm); }
    cm.set(id, (cm.get(id) || 0) + 1);
  }
  const grid = new Int32Array(dims[0] * dims[1] * dims[2]);
  const minFill = f === 1 ? 1 : 3;
  for (const [ci, cm] of cells) {
    let tot = 0, best = 0, bc = 0;
    for (const [id, c] of cm) { tot += c; if (c > bc) { bc = c; best = id; } }
    if (tot >= minFill) grid[ci] = best;
  }
  const at = (x, y, z) => (x < 0 || y < 0 || z < 0 || x >= dims[0] || y >= dims[1] || z >= dims[2]) ? 0 : grid[x + dims[0] * (y + dims[1] * z)];
  const pos = [], nor = [], col = [], part = [], emit = [], idx = [];
  const dface = [[2, 0], [0, 4], [4, 2]]; // axis -> FACE index for +/- : x:(0,1) y:(2,3) z:(4,5)
  for (let d = 0; d < 3; d++) {
    const u = (d + 1) % 3, w = (d + 2) % 3; const x = [0, 0, 0], q = [0, 0, 0]; q[d] = 1;
    const mask = new Int32Array(dims[u] * dims[w]);
    for (x[d] = -1; x[d] < dims[d];) {
      let n = 0;
      for (x[w] = 0; x[w] < dims[w]; x[w]++) for (x[u] = 0; x[u] < dims[u]; x[u]++) {
        const a = x[d] >= 0 ? at(x[0], x[1], x[2]) : 0;
        const b2 = x[d] < dims[d] - 1 ? at(x[0] + q[0], x[1] + q[1], x[2] + q[2]) : 0;
        mask[n++] = (a && !b2) ? a : (!a && b2) ? -b2 : 0;
      }
      x[d]++; n = 0;
      for (let jj = 0; jj < dims[w]; jj++) for (let ii = 0; ii < dims[u];) {
        const c = mask[n];
        if (c) {
          let wd = 1; while (ii + wd < dims[u] && mask[n + wd] === c) wd++;
          let ht = 1, done = false;
          for (; jj + ht < dims[w]; ht++) { for (let kk = 0; kk < wd; kk++) if (mask[n + kk + ht * dims[u]] !== c) { done = true; break; } if (done) break; }
          x[u] = ii; x[w] = jj;
          const du = [0, 0, 0], dv = [0, 0, 0]; du[u] = wd; dv[w] = ht;
          const v = info[Math.abs(c) - 1]; satBoost(teamColor(v.c, team, tmpC), sat);
          const pos0 = pos.length / 3; const s = c > 0 ? 1 : -1;
          const fi = d * 2 + (s > 0 ? 0 : 1); const sh = v.e ? 1 : FACE[fi] * shade;
          const corners = [[0, 0, 0], du, [du[0] + dv[0], du[1] + dv[1], du[2] + dv[2]], dv];
          for (const cc of corners) {
            const gx = x[0] + cc[0], gy = x[1] + cc[1], gz = x[2] + cc[2];
            pos.push((mn[0] + gx * f - cx) * vs, (mn[1] + gy * f - y0) * vs, (mn[2] + gz * f - cz) * vs);
            const nn = [0, 0, 0]; nn[d] = s; nor.push(nn[0], nn[1], nn[2]);
            col.push(tmpC.r * sh, tmpC.g * sh, tmpC.b * sh); part.push(v.p); emit.push(v.e);
          }
          if (s > 0) idx.push(pos0, pos0 + 1, pos0 + 2, pos0, pos0 + 2, pos0 + 3); else idx.push(pos0, pos0 + 2, pos0 + 1, pos0, pos0 + 3, pos0 + 2);
          for (let l = 0; l < ht; l++) for (let kk = 0; kk < wd; kk++) mask[n + kk + l * dims[u]] = 0;
          ii += wd; n += wd;
        } else { ii++; n++; }
      }
    }
  }
  return finishGeo(pos, nor, col, part, emit, idx);
}
function rotX(v, a) { const c = Math.cos(a), s = Math.sin(a); return [v[0], v[1] * c - v[2] * s, v[1] * s + v[2] * c]; }

/* Comic outline: per-vertex offset away from its part's centre (inverted-hull, works with instancing + shader animation) */
function addOutline(geo, vs, thick = 0.9) {
  const pos = geo.getAttribute('position'), pa = geo.getAttribute('aPart');
  const mn = {}, mx = {};
  for (let i = 0; i < pos.count; i++) {
    const p = pa ? pa.getX(i) : 0; if (!mn[p]) { mn[p] = [1e9, 1e9, 1e9]; mx[p] = [-1e9, -1e9, -1e9]; }
    for (let a = 0; a < 3; a++) { const v = pos.getComponent(i, a); if (v < mn[p][a]) mn[p][a] = v; if (v > mx[p][a]) mx[p][a] = v; }
  }
  const out = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const p = pa ? pa.getX(i) : 0;
    for (let a = 0; a < 3; a++) { const c = (mn[p][a] + mx[p][a]) / 2, size = mx[p][a] - mn[p][a]; out[i * 3 + a] = (pos.getComponent(i, a) - c) * (vs * thick / Math.max(vs * 4, size)); }
  }
  geo.setAttribute('aOut', new THREE.Float32BufferAttribute(out, 3));
  return geo;
}
/* Build the visual + stat bundle for one army */
function buildArmy(team, cfg) {
  const ty = TYPES[cfg.type], wp = WEAPONS[cfg.weapon], s = cfg.size;
  const isRecipe = !!ty.recipe, isHum = isRecipe && RECIPES[ty.recipe].template === 'humanoid';
  const ranged = !wp.melee;
  // weapon voxels are authored at base resolution: each one becomes a k^3 block on a detail-k model
  const dress = mm => {
    const mount = ranged ? mm.mountRanged : mm.mountMelee, k = mm.detail || 1;
    if (wp.vox) {
      const prevPart = mm.part; mm.use(mount.part);
      wp.vox((f0, f1, s0, s1, u0, u1, c, glow) => {
        for (let f = f0 * k; f < (f1 + 1) * k; f++) for (let ss = s0 * k; ss < (s1 + 1) * k; ss++) for (let u = u0 * k; u < (u1 + 1) * k; u++) {
          const x = mount.a[0] + f * mount.f[0] + u * mount.u[0] + ss * mount.s[0];
          const y = mount.a[1] + f * mount.f[1] + u * mount.u[1] + ss * mount.s[1];
          const z = mount.a[2] + f * mount.f[2] + u * mount.u[2] + ss * mount.s[2];
          mm.glow(!!glow); mm.set(x, y, z, typeof c === 'function' ? c(Math.floor(f / k), Math.floor(u / k), Math.floor(ss / k)) : c);
        }
        mm.glow(0);
      });
      mm.use(prevPart);
    }
    // aim pose for humanoids with ranged weapons: raise both arms
    if (ranged && mm.kind === 'humanoid') { mm.parts[mm.p.pArmL].base = -1.2; mm.parts[mm.p.pArmR].base = -1.5; }
    return mm;
  };
  // far LOD + debris from the base model; the near LOD (and pivots/muzzle) from the detail-2 model
  const m = dress(isRecipe ? recipeModel(ty.recipe, cfg) : MODELS[cfg.type]());
  const mHi = isRecipe ? dress(recipeModel(ty.recipe, { ...cfg, detail: 2 })) : m;
  const ctr = modelCenter(m), ctrHi = mHi === m ? ctr : modelCenter(mHi);
  const aim = ranged && m.kind === 'humanoid';
  const mount = ranged ? mHi.mountRanged : mHi.mountMelee, kHi = mHi.detail || 1;
  // muzzle in local metres
  let muz = [0, ctrHi.h * 0.6 * mHi.vs, ctrHi.cz];
  if (ranged) {
    const tip = (wp.tip || [10, 0.5, 0.5]).map(v => v * kHi + (kHi - 1) / 2);
    const a = mount.a;
    const vx = a[0] + tip[0] * mount.f[0] + tip[1] * mount.u[0] + tip[2] * mount.s[0] + 0.5;
    const vy = a[1] + tip[0] * mount.f[1] + tip[1] * mount.u[1] + tip[2] * mount.s[1] + 0.5;
    const vz = a[2] + tip[0] * mount.f[2] + tip[1] * mount.u[2] + tip[2] * mount.s[2] + 0.5;
    let pnt = [vx, vy, vz];
    const pr = mHi.parts[mount.part];
    if (pr.base) { const pv = pr.pivot; const r = rotX([pnt[0] - pv[0], pnt[1] - pv[1], pnt[2] - pv[2]], pr.base); pnt = [r[0] + pv[0], r[1] + pv[1], r[2] + pv[2]]; }
    muz = [(pnt[0] - ctrHi.cx) * mHi.vs, (pnt[1] - ctrHi.y0) * mHi.vs, (pnt[2] - ctrHi.cz) * mHi.vs];
  }
  // outline thickness follows the base voxel size so both LODs read the same
  // near LOD: greedy-merged faces (same colour/part/glow); the ±4 % per-voxel jitter moved to the shader (uVox = jit0)
  const geo0 = addOutline(meshGreedy(mHi, team, ctrHi, 1, 1.15, 1), m.vs);
  const fr = v => v - Math.floor(v), jit0 = [fr(ctrHi.cx), fr(ctrHi.y0), fr(ctrHi.cz), mHi.vs];
  const geo1 = addOutline(meshGreedy(m, team, ctr, m.vs < 0.08 ? 1 : 2, 1.15), m.vs);
  // voxel sample for debris
  const vox = []; const vcol = [];
  for (const [k, v] of m.map) { const [x, y, z] = DEC(k); vox.push((x + 0.5 - ctr.cx) * m.vs, (y + 0.5 - ctr.y0) * m.vs, (z + 0.5 - ctr.cz) * m.vs); teamColor(v.c, team, tmpC); vcol.push(tmpC.r, tmpC.g, tmpC.b); }
  // pivots in metres
  const pivots = [], roles = [], bases = [];
  for (let i = 0; i < 12; i++) {
    const pr = mHi.parts[i];
    if (pr) { pivots.push(new THREE.Vector3((pr.pivot[0] - ctrHi.cx) * mHi.vs, (pr.pivot[1] - ctrHi.y0) * mHi.vs, (pr.pivot[2] - ctrHi.cz) * mHi.vs)); roles.push(pr.role); bases.push(pr.base); }
    else { pivots.push(new THREE.Vector3()); roles.push(0); bases.push(0); }
  }
  const h = ctr.h * m.vs;
  const atkStyle = ranged ? 2 : m.atkStyle;
  const stats = {
    count: cfg.count, s, r: ty.r * s * (isHum ? (cfg.width || 1) : 1), speed: ty.speed * Math.sqrt(s), hp: ty.hp * s * s * s, mass: ty.mass * s * s,
    melee: !!wp.melee, area: !!(wp.cleave || ty.stomp || s >= 3), cleave: !!wp.cleave, stomp: !!ty.stomp,
    reach: (wp.reach || 0) * s, range: (wp.range || 0) * (1 + (s - 1) * 0.25),
    dmg: (wp.dmg) * ty.dmgMul * Math.pow(s, 1.5), cd: wp.cd, hitscan: !!wp.hitscan, acc: wp.acc || 1,
    proj: wp.proj || 0, pspeed: wp.pspeed || 0, aoe: (wp.aoe || 0) * (1 + (s - 1) * 0.35),
    knock: (ty.stomp ? Math.max(wp.knock, 6) : wp.knock) * Math.sqrt(s), arc: !!wp.arc, h: h * s,
    mx: muz[0] * s, my: muz[1] * s, mz: muz[2] * s, atkDur: wp.atkDur, giant: ty.r * s * (isHum ? (cfg.width || 1) : 1) > 1.95,
    turret: !!m.turret, bravery: s >= 3 ? 9 : ty.bravery, s, stride: 6.5 / (h * s), turn: 9 / Math.sqrt(s),
    // feature:support (Heiler & Aura): only types that have them, so every other army's stats stay as they were
    ...(ty.heal ? { heal: { r: ty.heal.r * Math.sqrt(s), hps: ty.heal.hps * s * s } } : {}),
    ...(ty.aura ? { aura: { r: ty.aura.r * Math.sqrt(s), speed: ty.aura.speed } } : {}),
  };
  return { team, cfg, ty, wp, m, ctr, geo0, geo1, jit0, vox: new Float32Array(vox), vcol: new Float32Array(vcol), voxN: vox.length / 3,
    pivots, roles, bases, aim, atkStyle, stats, h, debrisN: Math.round(ty.debris * Math.min(3, Math.sqrt(s))) };
}

export { AOF, BONE, BOOT, BOOT_D, CREST, CREST_D, DARK, DEC, FACE, GOLD, GOLD_D, KEY, LEATH, LEATH_D, MAIL1, MAIL2, MODELS, OLIVE, OLIVE_D, OLIVE_L, P, PRESETS, RECIPES, ROLE, RPROPS, SKIN, SKIN_D, SLIT, STEEL, STEEL_D, STEEL_L, T, T2, TEAMS, TL, TYPES, VARIANTS, VoxModel, WEAPONS, addOutline, buildArmy, chk, compileRecipe, finishGeo, hash3, headsTall, humanoid, hx, lum, mapColors, meshAO, meshGreedy, modelCenter, mutantN, rHex, rPrim, rProps, ramp, recipeModel, registerMutant, rotX, satBoost, teamColor, tmpC };
